/*
 This file is part of GNU Taler
 (C) 2019-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU Affero General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU Affero General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>

 SPDX-License-Identifier: AGPL-3.0-or-later
 */

/**
 * Types used by clients of the wallet.
 *
 * These types are defined in a separate file make tree shaking easier, since
 * some components use these types (via RPC) but do not depend on the wallet
 * code directly.
 *
 * @author Florian Dold <dold@taler.net>
 */

/**
 * Imports.
 */
import { AmountJson, codecForAmountString } from "./amounts.js";
import {
  Codec,
  Context,
  DecodingError,
  buildCodecForObject,
  buildCodecForUnion,
  codecForAny,
  codecForBoolean,
  codecForConstString,
  codecForEither,
  codecForList,
  codecForNumber,
  codecForString,
  codecForStringEnum,
  codecForStringUnion,
  codecOptional,
  renderContext,
} from "./codec.js";
import { canonicalizeBaseUrl } from "./helpers.js";
import { TaldirAlreadyPaidResponse } from "./types-taler-directory.js";
import { PaytoString, codecForPaytoString } from "./payto.js";
import { PerformanceTable } from "./performance.js";
import { QrCodeSpec } from "./qr.js";
import { AgeCommitmentProof } from "./taler-crypto.js";
import { TalerErrorCode } from "./taler-error-codes.js";
import { TalerUri, TalerUriString, TemplateParams } from "./taleruri.js";
import {
  AbsoluteTime,
  DurationUnitSpec,
  TalerPreciseTimestamp,
  TalerProtocolDuration,
  TalerProtocolTimestamp,
  codecForAbsoluteTime,
  codecForDuration,
  codecForTimestamp,
} from "./time.js";
import { BlindedDonationReceiptKeyPair } from "./types-donau.js";
import { WithdrawalOperationStatusFlag } from "./types-taler-bank-integration.js";
import {
  AmountString,
  CurrencySpecification,
  EddsaPrivateKeyString,
  EddsaPublicKeyString,
  EddsaSignatureString,
  HashCode,
  HashCodeString,
  RelativeTime,
  Timestamp,
  codecForEddsaPrivateKey,
} from "./types-taler-common.js";
import {
  AccountRestriction,
  CoinEnvelope,
  DenomKeyType,
  DenominationPubKey,
  ExchangeAuditor,
  ExchangeWithdrawValue,
  ExchangeRefundRequest,
  ExchangeWireAccount,
  PartialPeerContractTerms,
  PeerContractTerms,
  UnblindedDenominationSignature,
  codecForPartialPeerContractTerms,
  codecForPeerContractTerms,
} from "./types-taler-exchange.js";
import {
  MerchantContractTerms,
  MerchantContractTermsV0,
  MerchantContractTermsV1,
  MerchantInfo,
  TokenEnvelope,
  TokenIssuePublicKey,
  WalletTemplateDetailsResponse,
} from "./types-taler-merchant.js";
import {
  TransactionMajorState,
  TransactionMinorState,
  TransactionState,
  TransactionStateWildcard,
} from "./types-taler-wallet-transactions.js";

/**
 * Identifier for a transaction in the wallet.
 */
declare const __txId: unique symbol;
export type TransactionIdStr = `txn:${string}:${string}` & { [__txId]: true };

/**
 * Identifier for a pending task in the wallet.
 */
declare const __pndId: unique symbol;
export type PendingIdStr = `pnd:${string}:${string}` & { [__pndId]: true };

declare const __tmbId: unique symbol;
export type TombstoneIdStr = `tmb:${string}:${string}` & { [__tmbId]: true };

function codecForTransactionIdStr(): Codec<TransactionIdStr> {
  return {
    decode(x: any, c?: Context): TransactionIdStr {
      if (typeof x === "string" && x.startsWith("txn:")) {
        return x as TransactionIdStr;
      }
      throw new DecodingError(
        `expected string starting with "txn:" at ${renderContext(
          c,
        )} but got ${x}`,
      );
    },
  };
}

export function codecForCanonBaseUrl(): Codec<string> {
  return {
    decode(x: any, c?: Context): string {
      if (typeof x === "string") {
        const canon = canonicalizeBaseUrl(x);
        if (x !== canon) {
          throw new DecodingError(
            `expected canonicalized base URL at ${renderContext(
              c,
            )} but got value '${x}'`,
          );
        }
        return x;
      }
      throw new DecodingError(
        `expected base URL at ${renderContext(c)} but got type ${typeof x}`,
      );
    },
  };
}

export enum ScopeType {
  Global = "global",
  Exchange = "exchange",
  Auditor = "auditor",
  /**
   * Funds issued under a master public key the exchange has since replaced.
   *
   * A distinct type rather than an optional field on {@link ScopeInfoExchange}
   * on purpose: these funds must never be pooled with, or selected alongside,
   * funds under the key the exchange currently uses, and a scope that merely
   * carried an extra field would render as a second bucket with the same
   * label and would still be matched by an existing exchange-scoped filter.
   */
  ExchangeLegacyKeys = "exchange-legacy-keys",
}

export type ScopeInfoGlobal = { type: ScopeType.Global; currency: string };

export type ScopeInfoExchange = {
  type: ScopeType.Exchange;
  currency: string;
  url: string;
};

export type ScopeInfoAuditor = {
  type: ScopeType.Auditor;
  currency: string;
  url: string;
};

export type ScopeInfoExchangeLegacyKeys = {
  type: ScopeType.ExchangeLegacyKeys;
  currency: string;
  url: string;
  /** The superseded key the funds were issued under. */
  masterPub: string;
};

export type ScopeInfo =
  | ScopeInfoGlobal
  | ScopeInfoExchange
  | ScopeInfoAuditor
  | ScopeInfoExchangeLegacyKeys;

export const codecForScopeInfo = (): Codec<ScopeInfo> =>
  buildCodecForUnion<ScopeInfo>()
    .discriminateOn("type")
    .alternative(ScopeType.Global, codecForScopeInfoGlobal())
    .alternative(ScopeType.Exchange, codecForScopeInfoExchange())
    .alternative(ScopeType.Auditor, codecForScopeInfoAuditor())
    .alternative(
      ScopeType.ExchangeLegacyKeys,
      codecForScopeInfoExchangeLegacyKeys(),
    )
    .build("ScopeInfo");

export interface GetBalanceDetailRequest {
  currency: string;
}

export const codecForGetBalanceDetailRequest =
  (): Codec<GetBalanceDetailRequest> =>
    buildCodecForObject<GetBalanceDetailRequest>()
      .property("currency", codecForString())
      .build("GetBalanceDetailRequest");

/**
 * How the amount should be interpreted in a transaction
 * Effective = how the balance is change
 * Raw = effective amount without fee
 *
 * Depending on the transaction, raw can be higher than effective
 */
export enum TransactionAmountMode {
  Effective = "effective",
  Raw = "raw",
}

/**
 * @deprecated Use {@link CheckDepositRequest} for a concrete instructed
 * amount, or {@link GetMaxDepositAmountRequest} to query deposit limits.
 */
export interface ConvertAmountRequest {
  amount: AmountString;
  type: TransactionAmountMode;
  depositPaytoUri: PaytoString;
}

export const codecForConvertAmountRequest =
  buildCodecForObject<ConvertAmountRequest>()
    .property("amount", codecForAmountString())
    .property("depositPaytoUri", codecForPaytoString())
    .property(
      "type",
      codecForEither(
        codecForConstString(TransactionAmountMode.Raw),
        codecForConstString(TransactionAmountMode.Effective),
      ),
    )
    .build("ConvertAmountRequest");

export interface GetMaxDepositAmountRequest {
  /**
   * Currency to deposit.
   */
  currency: string;

  /**
   * Target bank account to deposit into.
   *
   * When omitted, wire-method eligibility, account restrictions and wire
   * fees cannot be reflected in the response.
   */
  depositPaytoUri?: string;

  /**
   * Restrict the deposit to a certain scope.
   */
  restrictScope?: ScopeInfo;
}

export const codecForGetMaxDepositAmountRequest =
  (): Codec<GetMaxDepositAmountRequest> =>
    buildCodecForObject<GetMaxDepositAmountRequest>()
      .property("currency", codecForString())
      .property("depositPaytoUri", codecOptional(codecForString()))
      .property("restrictScope", codecOptional(codecForScopeInfo()))
      .build("GetMaxDepositAmountRequest");

export interface GetMaxPeerPushDebitAmountRequest {
  currency: string;
  /**
   * Preferred exchange to use for the p2p payment.
   */
  exchangeBaseUrl?: string;
  restrictScope?: ScopeInfo;
}

export const codecForGetMaxPeerPushDebitAmountRequest =
  (): Codec<GetMaxPeerPushDebitAmountRequest> =>
    buildCodecForObject<GetMaxPeerPushDebitAmountRequest>()
      .property("currency", codecForString())
      .property("exchangeBaseUrl", codecOptional(codecForString()))
      .property("restrictScope", codecOptional(codecForScopeInfo()))
      .build("GetMaxPeerPushDebitRequest");

export enum DepositEligibilityReasonType {
  DirectDepositDisabled = "direct-deposit-disabled",
  ScopeRestricted = "scope-restricted",
  WireMethodUnsupported = "wire-method-unsupported",
  WireFeeUnavailable = "wire-fee-unavailable",
  DepositAccountRestricted = "deposit-account-restricted",
}

/** Reason why a ready, same-currency exchange cannot serve a deposit. */
export type DepositEligibilityReason =
  | { type: DepositEligibilityReasonType.DirectDepositDisabled }
  | {
      type: DepositEligibilityReasonType.ScopeRestricted;
      scopeInfo: ScopeInfo;
    }
  | {
      type: DepositEligibilityReasonType.WireMethodUnsupported;
      wireMethod: string;
    }
  | {
      type: DepositEligibilityReasonType.WireFeeUnavailable;
      wireMethod: string;
    }
  | {
      type: DepositEligibilityReasonType.DepositAccountRestricted;
      wireMethod: string;
      accountRestrictions: Record<string, AccountRestriction[]>;
    };

/** Maximum amounts and fees for one coherent deposit coin selection. */
export interface DepositMaximum {
  /** Gross target amount passed to CheckDeposit or CreateDepositGroup. */
  instructedAmount: AmountString;

  /**
   * Total balance effect on the wallet: instructed amount plus fees paid by
   * the customer and the cost of refreshing any change.
   */
  effectiveAmount: AmountString;

  /**
   * Amount expected to reach the destination account: instructed amount
   * minus fees covered by the counterparty.
   */
  rawAmount: AmountString;

  /** Total fees incurred by this deposit selection. */
  fees: DepositGroupFees;
}

export interface DepositExchangeDiagnostics {
  /** Maximum that can be deposited immediately. */
  material: DepositMaximum;

  /** Maximum including expected outputs of pending refresh operations. */
  available: DepositMaximum;

  /** Eligibility failures, in deterministic evaluation order. */
  reasons: DepositEligibilityReason[];
}

export interface GetMaxDepositAmountResponse {
  /** Maximum that can be deposited immediately. */
  material: DepositMaximum;

  /** Maximum including expected outputs of pending refresh operations. */
  available: DepositMaximum;

  /** Eligibility and maximum amounts for every ready same-currency exchange. */
  exchangeDiagnostics: Record<string, DepositExchangeDiagnostics>;
}

export interface GetMaxPeerPushDebitAmountResponse {
  effectiveAmount: AmountString;
  rawAmount: AmountString;
  exchangeBaseUrl?: string;
}

export interface AmountResponse {
  effectiveAmount: AmountString;
  rawAmount: AmountString;
}

export enum BalanceFlag {
  IncomingKyc = "incoming-kyc",
  IncomingAml = "incoming-aml",
  IncomingConfirmation = "incoming-confirmation",
  OutgoingKyc = "outgoing-kyc",
}

export interface WalletBalance {
  scopeInfo: ScopeInfo;
  available: AmountString;
  pendingIncoming: AmountString;
  pendingOutgoing: AmountString;

  flags: BalanceFlag[];

  /**
   * Available URLs for pages that list
   * where money in this scope can be spent.
   */
  shoppingUrls?: string[];

  /**
   * Are p2p payments disabled for this scope?
   */
  disablePeerPayments?: boolean;

  /**
   * Are wallet deposits enabled for this scope?
   */
  disableDirectDeposits?: boolean;
}

export const codecForScopeInfoGlobal = (): Codec<ScopeInfoGlobal> =>
  buildCodecForObject<ScopeInfoGlobal>()
    .property("currency", codecForString())
    .property("type", codecForConstString(ScopeType.Global))
    .build("ScopeInfoGlobal");

export const codecForScopeInfoExchange = (): Codec<ScopeInfoExchange> =>
  buildCodecForObject<ScopeInfoExchange>()
    .property("currency", codecForString())
    .property("type", codecForConstString(ScopeType.Exchange))
    .property("url", codecForString())
    .build("ScopeInfoExchange");

export const codecForScopeInfoAuditor = (): Codec<ScopeInfoAuditor> =>
  buildCodecForObject<ScopeInfoAuditor>()
    .property("currency", codecForString())
    .property("type", codecForConstString(ScopeType.Auditor))
    .property("url", codecForString())
    .build("ScopeInfoAuditor");

export const codecForScopeInfoExchangeLegacyKeys =
  (): Codec<ScopeInfoExchangeLegacyKeys> =>
    buildCodecForObject<ScopeInfoExchangeLegacyKeys>()
      .property("currency", codecForString())
      .property("type", codecForConstString(ScopeType.ExchangeLegacyKeys))
      .property("url", codecForString())
      .property("masterPub", codecForString())
      .build("ScopeInfoExchangeLegacyKeys");

export interface GetCurrencySpecificationRequest {
  scope: ScopeInfo;
}

export const codecForGetCurrencyInfoRequest =
  (): Codec<GetCurrencySpecificationRequest> =>
    buildCodecForObject<GetCurrencySpecificationRequest>()
      .property("scope", codecForScopeInfo())
      .build("GetCurrencySpecificationRequest");

export interface GetCurrencySpecificationResponse {
  currencySpecification: CurrencySpecification;
}

/**
 * Coin selection algorithm the wallet uses when spending.
 *
 * "legacy-2024" is the algorithm the wallet shipped with in 2024: strictly
 * largest-coin-first, without the exact-fit shortcut and the deposit-fee
 * allowance correction that were added later.  It is kept reachable because
 * test suites outside this repository (in particular the exchange's auditor
 * tests) pin the coin selections it produces.
 */
export type CoinSelectionAlgorithm = "default" | "legacy-2024";

export interface PartialWalletRunConfig {
  testing?: Partial<WalletRunConfig["testing"]>;
  features?: Partial<WalletRunConfig["features"]>;
  lazyTaskLoop?: Partial<WalletRunConfig["lazyTaskLoop"]>;
  logLevel?: Partial<WalletRunConfig["logLevel"]>;
}

export interface WalletRunConfig {
  /**
   * Unsafe options which it should only be used to create
   * testing environment.
   */
  testing: {
    devModeActive: boolean;
    insecureTrustExchange: boolean;
    preventThrottling: boolean;
    skipDefaults: boolean;
    emitObservabilityEvents?: boolean;

    /**
     * Coin selection algorithm to use when spending.
     *
     * Defaults to the TALER_WALLET_COINSEL environment variable, and to
     * "default" when that is unset.
     */
    coinSelectionAlgorithm: CoinSelectionAlgorithm;
  };

  /**
   * Configurations values that may be safe to show to the user
   */
  features: {
    allowHttp: boolean;

    /**
     * Migrate the wallet database to wallet-core's native sqlite schema,
     * replacing the IndexedDB emulation it has been stored in so far.
     *
     * Checked whenever the wallet is initialized, so a client that offers
     * this as a setting only has to initialize the wallet again for it to
     * take effect.  Off by default: the native schema is still experimental.
     *
     * The migration happens once and is not undone by clearing the flag --
     * afterwards the database *is* a native one.  It also only ever runs
     * where the host stores the wallet in a sqlite file, which excludes the
     * browser extension.
     */
    migrateNativeDb: boolean;

    /**
     * Use wallet-core's native sqlite schema when initializing a new, empty
     * database.
     *
     * Unlike migrateNativeDb, this never converts an existing IndexedDB
     * wallet.  Hosts without a native sqlite backend ignore the preference.
     */
    useNativeDb: boolean;
  };

  /**
   * Start processing tasks only when explicitly required, even after
   * init has been called.
   *
   * Useful when the wallet is started to make single read-only request,
   * as otherwise wallet-core starts making network request and process
   * unrelated pending tasks.
   */
  lazyTaskLoop: boolean;

  /**
   * Global log level.
   */
  logLevel: string;
}

export interface InitRequest {
  config?: PartialWalletRunConfig;
}

export const codecForInitRequest = (): Codec<InitRequest> =>
  buildCodecForObject<InitRequest>()
    .property("config", codecForAny())
    .build("InitRequest");

export type WalletDatabaseBackend = "indexeddb" | "sqlite";

export interface InitResponse {
  versionInfo: WalletCoreVersion;

  /** Database backend used by the initialized wallet. */
  databaseBackend: WalletDatabaseBackend;
}

/** Start an explicit migration from IndexedDB emulation to native sqlite. */
export interface MigrateDatabaseRequest {
  /** Enables progress correlation and cancellation through cancelProgressToken. */
  progressToken?: string;
}

export const codecForMigrateDatabaseRequest =
  (): Codec<MigrateDatabaseRequest> =>
    buildCodecForObject<MigrateDatabaseRequest>()
      .property("progressToken", codecOptional(codecForString()))
      .build("MigrateDatabaseRequest");

export interface MigrateDatabaseResponse {
  /** Whether this request changed the active database backend. */
  migrated: boolean;

  /** Database backend active after the request. */
  databaseBackend: WalletDatabaseBackend;
}

/**
 * Shorter version of stringifyScopeInfo
 */
export function stringifyScopeInfoShort(si: ScopeInfo): string {
  switch (si.type) {
    case ScopeType.Global:
      return `${si.currency}`;
    case ScopeType.Exchange:
      return `${si.currency}/${encodeURIComponent(si.url)}`;
    case ScopeType.Auditor:
      return `${si.currency}:${encodeURIComponent(si.url)}`;
    case ScopeType.ExchangeLegacyKeys:
      // The URL is percent-encoded and so contains no slash of its own,
      // which is what lets a third field be appended unambiguously.
      return `${si.currency}/${encodeURIComponent(si.url)}/${si.masterPub}`;
  }
}
export function parseScopeInfoShort(si: string): ScopeInfo | undefined {
  const indexOfColon = si.indexOf(":");
  const indexOfSlash = si.indexOf("/");
  if (indexOfColon === -1 && indexOfSlash === -1) {
    return {
      type: ScopeType.Global,
      currency: si,
    };
  }
  if (indexOfColon > 0) {
    return {
      type: ScopeType.Auditor,
      currency: si.substring(0, indexOfColon),
      url: decodeURIComponent(si.substring(indexOfColon + 1)),
    };
  }
  if (indexOfSlash > 0) {
    const currency = si.substring(0, indexOfSlash);
    const rest = si.substring(indexOfSlash + 1);
    const sep = rest.indexOf("/");
    if (sep > 0) {
      return {
        type: ScopeType.ExchangeLegacyKeys,
        currency,
        url: decodeURIComponent(rest.substring(0, sep)),
        masterPub: rest.substring(sep + 1),
      };
    }
    return {
      type: ScopeType.Exchange,
      currency,
      url: decodeURIComponent(rest),
    };
  }
  return undefined;
}

/**
 * Encode scope info as a string.
 *
 * Format must be stable as it's used in the database.
 */
export function stringifyScopeInfo(si: ScopeInfo): string {
  switch (si.type) {
    case ScopeType.Global:
      return `taler-si:global/${si.currency}`;
    case ScopeType.Auditor:
      return `taler-si:auditor/${si.currency}/${encodeURIComponent(si.url)}`;
    case ScopeType.Exchange:
      return `taler-si:exchange/${si.currency}/${encodeURIComponent(si.url)}`;
    case ScopeType.ExchangeLegacyKeys:
      // A prefix of its own, so that every scope string written before this
      // type existed still encodes to exactly the bytes it did before and no
      // stored record has to be rewritten.
      return `taler-si:exchange-legacy/${si.currency}/${encodeURIComponent(
        si.url,
      )}/${si.masterPub}`;
  }
}

export interface DonauSummaryItem {
  /** Base URL of the donau service. */
  donauBaseUrl: string;
  /** Legal domain of the donau service (if available). */
  legalDomain?: string;
  /** Year of the donation(s). */
  year: number;
  /**
   * Sum of donation receipts we received from merchants in the
   * applicable year.
   */
  amountReceiptsAvailable: AmountString;
  /**
   * Sum of donation receipts that were already submitted
   * to the donau in the applicable year.
   */
  amountReceiptsSubmitted: AmountString;
  /**
   * Amount of the latest available statement. Missing if no statement
   * was requested yet.
   */
  amountStatement?: AmountString;
}

/**
 * Response to a getBalances request.
 */
export interface BalancesResponse {
  /** Electronic cash balances, per currency scope. */
  balances: WalletBalance[];
  /** Does the user have money from an exchange other than demo or test? */
  haveProdBalance: boolean;
  /* Summary of donations, per donau/year/currency. */
  donauSummary?: DonauSummaryItem[];
}

/**
 * Status of a coin.
 */
export enum CoinStatus {
  /**
   * Withdrawn and never shown to anybody.
   */
  Fresh = "fresh",

  /**
   * Coin was lost as the denomination is not usable anymore.
   */
  DenomLoss = "denom-loss",

  /**
   * Fresh, but currently marked as "suspended", thus won't be used
   * for spending.  Used for testing.
   */
  FreshSuspended = "fresh-suspended",

  /**
   * A coin that has been spent and refreshed.
   */
  Dormant = "dormant",
}

export type WalletCoinHistoryItem =
  | {
      type: "withdraw";
      transactionId: TransactionIdStr;
    }
  | {
      type: "spend";
      transactionId: TransactionIdStr;
      amount: AmountString;
    }
  | {
      type: "refresh";
      transactionId: TransactionIdStr;
      amount: AmountString;
    }
  | {
      type: "recoup";
      transactionId: TransactionIdStr;
      amount: AmountString;
    }
  | {
      type: "refund";
      transactionId: TransactionIdStr;
      amount: AmountString;
    };

/**
 * Easy to process format for the public data of coins
 * managed by the wallet.
 */
export interface CoinDumpJson {
  coins: Array<{
    /**
     * The coin's denomination's public key.
     */
    denomPub: DenominationPubKey;
    /**
     * Hash of denom_pub.
     */
    denomPubHash: string;
    /**
     * Value of the denomination (without any fees).
     */
    denomValue: string;
    /**
     * Public key of the coin.
     */
    coinPub: string;
    /**
     * Base URL of the exchange for the coin.
     */
    exchangeBaseUrl: string;
    /**
     * Public key of the parent coin.
     * Only present if this coin was obtained via refreshing.
     */
    refreshParentCoinPub: string | undefined;
    /**
     * Public key of the reserve for this coin.
     * Only present if this coin was obtained via refreshing.
     */
    withdrawalReservePub: string | undefined;
    /**
     * Status of the coin.
     */
    coinStatus: CoinStatus;
    /**
     * Information about the age restriction
     */
    ageCommitmentProof: AgeCommitmentProof | undefined;
    history: WalletCoinHistoryItem[];
  }>;
}

export enum ConfirmPayResultType {
  Done = "done",
  Pending = "pending",
}

/**
 * Result for confirmPay
 */
export interface ConfirmPayResultDone {
  type: ConfirmPayResultType.Done;
  contractTerms: MerchantContractTermsV0;
  transactionId: TransactionIdStr;
}

export interface ConfirmPayResultPending {
  type: ConfirmPayResultType.Pending;
  transactionId: TransactionIdStr;
  lastError?: TalerErrorDetail | undefined;
}

export const codecForTalerErrorDetail = (): Codec<TalerErrorDetail> =>
  buildCodecForObject<TalerErrorDetail>()
    .property("code", codecForNumber())
    .property("when", codecOptional(codecForAbsoluteTime))
    .property("hint", codecOptional(codecForString()))
    .build("TalerErrorDetail");

export type ConfirmPayResult = ConfirmPayResultDone | ConfirmPayResultPending;

export enum InsufficientBalanceHint {
  /**
   *  Merchant doesn't accept money from exchange(s) that the wallet supports.
   */
  MerchantAcceptInsufficient = "merchant-accept-insufficient",

  /**
   * Merchant accepts funds from a matching exchange, but the funds can't be
   * deposited with the wire method.
   */
  MerchantDepositInsufficient = "merchant-deposit-insufficient",

  /**
   * While in principle the balance is sufficient,
   * the age restriction on coins causes the spendable
   * balance to be insufficient.
   */
  AgeRestricted = "age-restricted",

  /**
   * Wallet has enough available funds,
   * but the material funds are insufficient. Usually because there is a
   * pending refresh operation.
   */
  WalletBalanceMaterialInsufficient = "wallet-balance-material-insufficient",

  /**
   * The wallet simply doesn't have enough available funds.
   * This is the "obvious" case of insufficient balance.
   */
  WalletBalanceAvailableInsufficient = "wallet-balance-available-insufficient",

  /**
   * Exchange is missing the global fee configuration, thus fees are unknown
   * and funds from this exchange can't be used for p2p payments.
   */
  ExchangeMissingGlobalFees = "exchange-missing-global-fees",

  /**
   * Even though the balance looks sufficient for the instructed amount,
   * the fees can be covered by neither the merchant nor the remaining wallet
   * balance.
   */
  FeesNotCovered = "fees-not-covered",
}

/**
 * Machine-readable reasons that prevented a requested coin selection.
 *
 * Unlike {@link InsufficientBalanceHint}, these values are exhaustive and can
 * be reported together.  Consumers must branch on the discriminator instead
 * of assuming that the first entry is the only cause.
 */
export enum CoinSelectionFailureReasonType {
  AvailableBalanceInsufficient = "available-balance-insufficient",
  PendingRefresh = "pending-refresh",
  MinimumAge = "minimum-age",
  ScopeRestricted = "scope-restricted",
  ReceiverNotAccepted = "receiver-not-accepted",
  ReceiverExchangeMasterPubMismatch = "receiver-exchange-master-pub-mismatch",
  WireMethodUnsupported = "wire-method-unsupported",
  WireFeeUnavailable = "wire-fee-unavailable",
  DepositAccountRestricted = "deposit-account-restricted",
  ExchangeGlobalFeesUnavailable = "exchange-global-fees-unavailable",
  FeesNotCovered = "fees-not-covered",
  BalanceFragmented = "balance-fragmented",
  SupersededExchangeMasterPub = "superseded-exchange-master-pub",
  SelectionFailed = "selection-failed",
}

export type CoinSelectionFailureReason =
  | {
      type: CoinSelectionFailureReasonType.AvailableBalanceInsufficient;
      amountAvailable: AmountString;
    }
  | {
      type: CoinSelectionFailureReasonType.PendingRefresh;
      amountPendingRefresh: AmountString;
    }
  | {
      type: CoinSelectionFailureReasonType.MinimumAge;
      requiredMinimumAge: number;
      amountAgeAcceptable: AmountString;
    }
  | {
      type: CoinSelectionFailureReasonType.ScopeRestricted;
      scopeInfo: ScopeInfo;
    }
  | { type: CoinSelectionFailureReasonType.ReceiverNotAccepted }
  | {
      type: CoinSelectionFailureReasonType.ReceiverExchangeMasterPubMismatch;
      walletMasterPub: string;
      receiverMasterPubs: string[];
    }
  | {
      type: CoinSelectionFailureReasonType.WireMethodUnsupported;
      wireMethod: string;
    }
  | {
      type: CoinSelectionFailureReasonType.WireFeeUnavailable;
      wireMethod: string;
    }
  | {
      type: CoinSelectionFailureReasonType.DepositAccountRestricted;
      wireMethod: string;
      accountRestrictions: Record<string, AccountRestriction[]>;
    }
  | { type: CoinSelectionFailureReasonType.ExchangeGlobalFeesUnavailable }
  | {
      type: CoinSelectionFailureReasonType.FeesNotCovered;
      maximumPayableAmount: AmountString;
    }
  | {
      type: CoinSelectionFailureReasonType.BalanceFragmented;
      combinedMaximumPayableAmount: AmountString;
    }
  | {
      type: CoinSelectionFailureReasonType.SupersededExchangeMasterPub;
      amountAffected: AmountString;
    }
  | { type: CoinSelectionFailureReasonType.SelectionFailed };

/**
 * Balance amounts before age, receiver, wire and fee restrictions.
 *
 * The terminology follows balance.ts: pending withdrawals and peer credits
 * are pending incoming balance and are not included here.  Only effective
 * outputs expected from unfinished refreshes bridge material to available.
 */
export interface CoinSelectionBalanceSnapshot {
  /** Balance that the wallet believes it can spend immediately. */
  material: AmountString;

  /** Expected effective output of unfinished refresh operations. */
  pendingRefresh: AmountString;

  /** Material balance plus pending refresh output. */
  available: AmountString;
}

export interface CoinSelectionExchangeFailureDiagnostics {
  /** Balance held at this exchange before payment restrictions. */
  balance: CoinSelectionBalanceSnapshot;

  /** Maximum contribution selectable from this exchange for this request. */
  maximumPayableAmount: AmountString;

  /** Exchange-local reasons, in deterministic evaluation order. */
  reasons: CoinSelectionFailureReason[];
}

/** Structured explanation emitted by current wallet-core versions. */
export interface PaymentInsufficientBalanceStructuredDetails {
  /** Balance in the requested sender scope before payment restrictions. */
  balance: CoinSelectionBalanceSnapshot;

  /**
   * Maximum contribution attainable under the failed request's actual
   * restrictions and fee policy.  For peer payments this is the maximum at
   * one exchange, since peer payments cannot combine exchanges.
   */
  maximumPayableAmount: AmountString;

  /** Operation-wide reasons, in deterministic evaluation order. */
  reasons: CoinSelectionFailureReason[];

  /** Detailed analysis for every same-currency exchange known to the wallet. */
  exchanges: Record<string, CoinSelectionExchangeFailureDiagnostics>;
}

/**
 * Request context and compatibility fields shared by old and current
 * insufficient-balance responses.
 */
interface PaymentInsufficientBalanceCompatibilityDetails {
  /**
   * Amount requested by the merchant.
   */
  amountRequested: AmountString;

  /**
   * Wire method for the requested payment, only applicable
   * for merchant payments.
   */
  wireMethod?: string | undefined;

  /**
   * Hint as to why the balance is insufficient.
   *
   * If this hint is not provided, the balance hints of
   * the individual exchanges should be shown, as the overall
   * reason might be a combination of the reasons for different exchanges.
   *
   * Compatibility-only and planned for removal after consumers migrate.
   *
   * @deprecated (2026-08-19) Use reasons.
   */
  causeHint?: InsufficientBalanceHint;

  /**
   * Balance of type "available" (see balance.ts for definition).
   *
   * Compatibility-only and planned for removal after consumers migrate.
   *
   * @deprecated (2026-08-19) Use balance.available.
   */
  balanceAvailable: AmountString;

  /**
   * Balance of type "material" (see balance.ts for definition).
   *
   * Compatibility-only and planned for removal after consumers migrate.
   *
   * @deprecated (2026-08-19) Use balance.material.
   */
  balanceMaterial: AmountString;

  /**
   * Balance of type "age-acceptable" (see balance.ts for definition).
   *
   * Compatibility-only and planned for removal after consumers migrate.
   *
   * @deprecated (2026-08-19) Use reasons and exchanges.
   */
  balanceAgeAcceptable: AmountString;

  /**
   * Balance of type "receiver-acceptable" (see balance.ts for definition).
   *
   * Compatibility-only and planned for removal after consumers migrate.
   *
   * @deprecated (2025-12-05) Use reasons and exchanges.
   */
  balanceReceiverAcceptable: AmountString;

  /**
   * Balance of type "receiver-exchange-url-acceptable" (see balance.ts for definition).
   *
   * Compatibility-only and planned for removal after consumers migrate.
   *
   * @deprecated (2026-08-19) Use reasons and exchanges.
   */
  balanceReceiverExchangeUrlAcceptable: AmountString;

  /**
   * Balance of type "receiver-exchange-pub-acceptable" (see balance.ts for definition).
   *
   * Compatibility-only and planned for removal after consumers migrate.
   *
   * @deprecated (2026-08-19) Use reasons and exchanges.
   */
  balanceReceiverExchangePubAcceptable: AmountString;

  /**
   * Balance of type "receiver-auditor-url-acceptable" (see balance.ts for definition).
   *
   * Compatibility-only and planned for removal after consumers migrate.
   *
   * @deprecated (2026-08-19) Use reasons and exchanges.
   */
  balanceReceiverAuditorUrlAcceptable: AmountString;

  /**
   * Balance of type "merchant-depositable" (see balance.ts for definition).
   *
   * Compatibility-only and planned for removal after consumers migrate.
   *
   * @deprecated (2026-08-19) Use maximumPayableAmount and reasons.
   */
  balanceReceiverDepositable: AmountString;

  /**
   * Compatibility-only and planned for removal after consumers migrate.
   *
   * @deprecated (2026-08-19) Use maximumPayableAmount and reasons.
   */
  balanceExchangeDepositable: AmountString;

  /**
   * Maximum effective amount that the wallet can spend,
   * when all fees are paid by the wallet.
   *
   * Compatibility-only and planned for removal after consumers migrate.
   *
   * @deprecated (2026-08-19) Use maximumPayableAmount.
   */
  maxEffectiveSpendAmount: AmountString;

  /**
   * Compatibility-only and planned for removal after consumers migrate.
   *
   * @deprecated (2026-08-19) Use exchanges.
   */
  perExchange: {
    [url: string]: {
      /**
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2026-08-19) Use exchanges[url].balance.available.
       */
      balanceAvailable: AmountString;

      /**
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2026-08-19) Use exchanges[url].balance.material.
       */
      balanceMaterial: AmountString;

      /**
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2026-08-19) Use exchanges[url].maximumPayableAmount and reasons.
       */
      balanceExchangeDepositable: AmountString;

      /**
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2026-08-19) Use exchanges[url].reasons.
       */
      balanceAgeAcceptable: AmountString;

      /**
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2025-12-05) Use exchanges[url].reasons.
       */
      balanceReceiverAcceptable: AmountString;

      /**
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2026-08-19) Use exchanges[url].reasons.
       */
      balanceReceiverExchangeUrlAcceptable: AmountString;

      /**
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2026-08-19) Use exchanges[url].reasons.
       */
      balanceReceiverExchangePubAcceptable: AmountString;

      /**
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2026-08-19) Use exchanges[url].reasons.
       */
      balanceReceiverAuditorUrlAcceptable: AmountString;

      /**
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2026-08-19) Use exchanges[url].maximumPayableAmount and reasons.
       */
      balanceReceiverDepositable: AmountString;

      /**
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2026-08-19) Use exchanges[url].maximumPayableAmount.
       */
      maxEffectiveSpendAmount: AmountString;

      /**
       * The exchange master public key configured by the merchant
       * backend differs from the one of the coins stored in the wallet.
       *
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2026-08-19) Use the receiver-exchange-master-pub-mismatch reason.
       */
      exchangeMasterPubMismatch: boolean;

      /**
       * Exchange doesn't have global fees configured for the relevant year,
       * p2p payments aren't possible.
       *
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2025-02-18) Use the exchange-global-fees-unavailable reason.
       */
      missingGlobalFees: boolean;

      /**
       * Hint that UIs should show to explain the insufficient
       * balance.
       *
       * Compatibility-only and planned for removal after consumers migrate.
       *
       * @deprecated (2026-08-19) Use exchanges[url].reasons.
       */
      causeHint?: InsufficientBalanceHint | undefined;
    };
  };
}

interface PaymentInsufficientBalanceLegacyOnly {
  balance?: undefined;
  maximumPayableAmount?: undefined;
  reasons?: undefined;
  exchanges?: undefined;
}

/**
 * Detailed reason for why the wallet's balance is insufficient.
 *
 * Current wallet-core versions emit all structured fields.  The legacy-only
 * alternative lets clients continue decoding responses from older cores
 * without allowing partially populated structured diagnostics.
 */
export type PaymentInsufficientBalanceDetails =
  PaymentInsufficientBalanceCompatibilityDetails &
    (
      | PaymentInsufficientBalanceStructuredDetails
      | PaymentInsufficientBalanceLegacyOnly
    );

export interface PaymentTokenAvailabilityDetails {
  /**
   * Number of tokens requested by the merchant.
   */
  tokensRequested: number;

  /**
   * Number of tokens available to use.
   */
  tokensAvailable: number;

  /**
   * Number of tokens for which the merchant is unexpected.
   *
   * Can be used to pay (i.e. with forced selection),
   * but a warning should be displayed to the user.
   */
  tokensUnexpected: number;

  /**
   * Number of tokens for which the merchant is untrusted.
   *
   * Cannot be used to pay, so an error should be displayed.
   */
  tokensUntrusted: number;

  perTokenFamily: {
    [slug: string]: {
      causeHint?: TokenAvailabilityHint;
      requested: number;
      available: number;
      unexpected: number;
      untrusted: number;
    };
  };
}

export enum TokenAvailabilityHint {
  WalletTokensAvailableInsufficient = "wallet-tokens-available-insufficient",
  MerchantUnexpected = "merchant-unexpected",
  MerchantUntrusted = "merchant-untrusted",
}

export interface PreparePayV2Result {
  transactionId: TransactionIdStr;
}

/** Information needed to redeem a paid Paivana order for an access cookie. */
export interface PaivanaRedemption {
  /** Canonical HTTP(S) URL of the protected resource. */
  url: string;

  /** Crockford-base32 encoded 16-byte client nonce. */
  nonce: string;

  /** End of the access period used to derive the Paivana session ID. */
  expiration: TalerProtocolTimestamp;
}

export interface PreparePayForPaivanaRequest {
  url: string;
  progressToken?: string;
}

export interface PreparePayForPaivanaResult {
  transactionId: TransactionIdStr;
  paivana: PaivanaRedemption;
}

export interface GetPaivanaCookieRequest {
  transactionId: TransactionIdStr;
  paivana: PaivanaRedemption;
}

export interface GetPaivanaCookieResult {
  /** Plain Cookie request-header value, without Set-Cookie attributes. */
  cookie: string;
}

export const codecForPaivanaRedemption = (): Codec<PaivanaRedemption> =>
  buildCodecForObject<PaivanaRedemption>()
    .property("url", codecForString())
    .property("nonce", codecForString())
    .property("expiration", codecForTimestamp)
    .build("PaivanaRedemption");

export const codecForPreparePayForPaivanaRequest =
  (): Codec<PreparePayForPaivanaRequest> =>
    buildCodecForObject<PreparePayForPaivanaRequest>()
      .property("url", codecForString())
      .property("progressToken", codecOptional(codecForString()))
      .build("PreparePayForPaivanaRequest");

export const codecForGetPaivanaCookieRequest =
  (): Codec<GetPaivanaCookieRequest> =>
    buildCodecForObject<GetPaivanaCookieRequest>()
      .property("transactionId", codecForTransactionIdStr())
      .property("paivana", codecForPaivanaRedemption())
      .build("GetPaivanaCookieRequest");

export interface BankWithdrawDetails {
  status: WithdrawalOperationStatusFlag;
  currency: string;
  amount: AmountJson | undefined;
  editableAmount: boolean;
  maxAmount: AmountJson | undefined;
  wireFee: AmountJson | undefined;
  senderWire?: string;
  exchange?: string;
  editableExchange: boolean;
  confirmTransferUrl?: string;
  wireTypes: string[];
  operationId: string;
  apiBaseUrl: string;
}

export interface AcceptWithdrawalResponse {
  confirmTransferUrl?: string;
  transactionId: TransactionIdStr;
}

export interface TalerErrorDetail {
  code: TalerErrorCode;
  when?: AbsoluteTime;
  hint?: string;
  [x: string]: unknown;
}

/**
 * Minimal information needed about a planchet for unblinding a signature.
 *
 * Can be a withdrawal/refresh planchet.
 */
export interface PlanchetUnblindInfo {
  denomPub: DenominationPubKey;
  blindingKey: string;
  exchangeWithdrawValues: ExchangeWithdrawValue;
}

export interface WithdrawalPlanchet {
  coinPub: string;
  coinPriv: string;
  reservePub: string;
  denomPubHash: string;
  denomPub: DenominationPubKey;
  blindingKey: string;
  withdrawSig: string;
  coinEv: CoinEnvelope;
  coinValue: AmountJson;
  coinEvHash: string;
  ageCommitmentProof?: AgeCommitmentProof;
  exchangeWithdrawValues: ExchangeWithdrawValue;
}

export interface PlanchetCreationRequest {
  secretSeed: string;
  coinIndex: number;
  value: AmountJson;
  feeWithdraw: AmountJson;
  denomPub: DenominationPubKey;
  reservePub: string;
  reservePriv: string;
  restrictAge?: number;
  exchangeWithdrawValues?: ExchangeWithdrawValue;
  csNonce?: string;
}

/**
 * Minimal information needed about a slate for unblinding a signature.
 */
export interface SlateUnblindInfo {
  tokenIssuePub: TokenIssuePublicKey;
  blindingKey: string;
}

export interface Slate {
  tokenPub: string;
  tokenPriv: string;
  tokenIssuePub: TokenIssuePublicKey;
  tokenIssuePubHash: string;
  tokenWalletData: PayWalletData;
  tokenEv: TokenEnvelope;
  tokenEvHash: string;
  blindingKey: string;
}

export interface SlateCreationRequest {
  secretSeed: string;
  choiceIndex: number;
  outputIndex: number;
  repeatIndex: number;
  tokenIssuePub: TokenIssuePublicKey;
  genTokenUseSig: boolean;
  contractTerms: MerchantContractTermsV1;
  contractTermsHash: string;
}

export interface SignTokenUseRequest {
  tokenUsePriv: string;
  walletDataHash: string;
  contractTermsHash: string;
}

/**
 * Reasons for why a coin is being refreshed.
 */
export enum RefreshReason {
  Manual = "manual",
  PayMerchant = "pay-merchant",
  PayDeposit = "pay-deposit",
  PayPeerPush = "pay-peer-push",
  PayPeerPull = "pay-peer-pull",
  Refund = "refund",
  AbortPay = "abort-pay",
  AbortDeposit = "abort-deposit",
  AbortPeerPushDebit = "abort-peer-push-debit",
  AbortPeerPullDebit = "abort-peer-pull-debit",
  Recoup = "recoup",
  BackupRestored = "backup-restored",
  Scheduled = "scheduled",
}

/**
 * Request to refresh a single coin.
 */
export interface CoinRefreshRequest {
  readonly coinPub: string;
  readonly amount: AmountString;
  readonly refundRequest?: ExchangeRefundRequest;
}

/**
 * Private data required to make a deposit permission.
 */
export interface DepositInfo {
  exchangeBaseUrl: string;
  contractTermsHash: string;
  coinPub: string;
  coinPriv: string;
  spendAmount: AmountJson;
  timestamp: TalerProtocolTimestamp;
  refundDeadline: TalerProtocolTimestamp;
  merchantPub: string;
  feeDeposit: AmountJson;
  wireInfoHash: string;
  denomKeyType: DenomKeyType;
  denomPubHash: string;
  denomSig: UnblindedDenominationSignature;

  requiredMinimumAge?: number;

  ageCommitmentProof?: AgeCommitmentProof;

  walletDataHash?: string;
}

export interface ExchangesListResponse {
  exchanges: ExchangeListItem[];
}

export interface ListExchangesRequest {
  /**
   * Filter results to only include exchanges in the given scope.
   */
  filterByScope?: ScopeInfo;

  /**
   * Filter results to only include exchanges
   * with the given status.
   */
  filterByExchangeEntryStatus?: ExchangeEntryStatus;

  /**
   * Filter results to only include exchanges with the
   * given type.
   */
  filterByType?: ExchangeType;
}

export type ExchangeType = "demo" | "prod";

export const codecForExchangeType = () => codecForStringUnion("demo", "prod");

export const codecForListExchangesRequest = (): Codec<ListExchangesRequest> =>
  buildCodecForObject<ListExchangesRequest>()
    .property("filterByScope", codecOptional(codecForScopeInfo()))
    .property(
      "filterByExchangeEntryStatus",
      codecOptional(codecForExchangeEntryStatus()),
    )
    .property("filterByType", codecOptional(codecForExchangeType()))
    .build("ListExchangesRequest");

export interface ExchangeDetailedResponse {
  exchange: ExchangeFullDetails;
}

export interface AddContactRequest {
  contact: ContactEntry;
}

export interface DeleteContactRequest {
  contact: ContactEntry;
}

export interface ContactListResponse {
  contacts: ContactEntry[];
}

export type TaldirRegistrationResponse =
  | TaldirAlreadyPaidResponse
  | EmptyObject;

export interface TaldirRegistrationCompletionRequest {
  alias: string;
  aliasType: string;
  challenge: string;
  taldirBaseUrl: string;
  targetUri: string;
}

export const codecForTaldirRegistrationCompletionRequest =
  (): Codec<TaldirRegistrationCompletionRequest> =>
    buildCodecForObject<TaldirRegistrationCompletionRequest>()
      .property("targetUri", codecForString())
      .property("taldirBaseUrl", codecForString())
      .property("alias", codecForString())
      .property("aliasType", codecForString())
      .property("challenge", codecForString())
      .build("TaldirRegistrationCompletionRequest");

export interface TaldirRegistrationRequest {
  alias: string;
  aliasType: string;
  targetUri: string;
  taldirBaseUrl: string;
  duration: RelativeTime;
}

export const codecForTaldirRegistrationRequest =
  (): Codec<TaldirRegistrationRequest> =>
    buildCodecForObject<TaldirRegistrationRequest>()
      .property("targetUri", codecForString())
      .property("taldirBaseUrl", codecForString())
      .property("alias", codecForString())
      .property("aliasType", codecForString())
      .property("duration", codecForDuration)
      .build("TaldirRegistrationRequest");

export interface TaldirLookupRequest {
  alias: string;
  aliasType: string;
  taldirBaseUrl: string;
}

export const codecForTaldirLookupRequest = (): Codec<TaldirLookupRequest> =>
  buildCodecForObject<TaldirLookupRequest>()
    .property("taldirBaseUrl", codecForString())
    .property("alias", codecForString())
    .property("aliasType", codecForString())
    .build("TaldirLookupRequest");

export interface TaldirLookupResponse {
  targetUri?: string;
}

export interface GetMailboxResponse {
  mailboxConfiguration?: MailboxConfiguration;
}

export interface MailboxMessageRecordsResponse {
  messages: MailboxMessageRecord[];
}

export interface MailboxBaseUrl {
  mailboxBaseUrl: string;
}

export const codecForMailboxBaseUrl = (): Codec<MailboxBaseUrl> =>
  buildCodecForObject<MailboxBaseUrl>()
    .property("mailboxBaseUrl", codecForString())
    .build("MailboxBaseUrl");

export interface MailboxConfiguration {
  mailboxBaseUrl: string;
  privateKey: EddsaPrivateKeyString;
  privateEncryptionKey: string;
  expiration: Timestamp;
  hAddress: string;
  payUri?: TalerUri;
}

export const codecForMailboxConfiguration = (): Codec<MailboxConfiguration> =>
  buildCodecForObject<MailboxConfiguration>()
    .property("mailboxBaseUrl", codecForString())
    .property("hAddress", codecForString())
    .property("privateEncryptionKey", codecForString())
    .property("privateKey", codecForEddsaPrivateKey())
    .property("expiration", codecForTimestamp)
    .build("MailboxConfiguration");

export interface SendTalerUriMailboxMessageRequest {
  contact: ContactEntry;
  talerUri: string;
}

export interface AddMailboxMessageRequest {
  message: MailboxMessageRecord;
}

export interface DeleteMailboxMessageRequest {
  message: MailboxMessageRecord;
}

export interface MailboxMessagesResponse {
  messages: MailboxMessageRecord[];
}

export const codecForContactEntry = (): Codec<ContactEntry> =>
  buildCodecForObject<ContactEntry>()
    .property("alias", codecForString())
    .property("aliasType", codecForString())
    .property("mailboxBaseUri", codecForString())
    .property("mailboxAddress", codecForString())
    .property("source", codecForString())
    .property("petname", codecForString())
    .build("ContactListItem");

export const codecForAddContactRequest = (): Codec<AddContactRequest> =>
  buildCodecForObject<AddContactRequest>()
    .property("contact", codecForContactEntry())
    .build("AddContactRequest");

export const codecForDeleteContactRequest = (): Codec<DeleteContactRequest> =>
  buildCodecForObject<DeleteContactRequest>()
    .property("contact", codecForContactEntry())
    .build("DeleteContactRequest");

export const codecForAddMailboxMessageRequest =
  (): Codec<AddMailboxMessageRequest> =>
    buildCodecForObject<AddMailboxMessageRequest>()
      .property("message", codecForAny())
      .build("AddContactRequest");

export const codecForDeleteMailboxMessageRequest =
  (): Codec<DeleteMailboxMessageRequest> =>
    buildCodecForObject<DeleteMailboxMessageRequest>()
      .property("message", codecForAny())
      .build("DeleteContactRequest");

export const codecForSendTalerUriMailboxMessageRequest =
  (): Codec<SendTalerUriMailboxMessageRequest> =>
    buildCodecForObject<SendTalerUriMailboxMessageRequest>()
      .property("contact", codecForContactEntry())
      .property("talerUri", codecForString())
      .build("SendTalerUriMailboxMessageRequest");

export interface WalletCoreVersion {
  implementationSemver: string;
  implementationGitHash: string;

  /**
   * Wallet-core protocol version supported by this implementation
   * of the API ("server" version).
   */
  version: string;
  exchange: string;
  merchant: string;

  bankIntegrationApiRange: string;
  bankConversionApiRange: string;
  corebankApiRange: string;

  /**
   * @deprecated as bank was split into multiple APIs with separate versioning
   */
  bank: string;

  /**
   * @deprecated
   */
  hash: string | undefined;

  /**
   * @deprecated will be removed
   */
  devMode: boolean;
}

export interface WalletBankAccountInfo {
  bankAccountId: string;

  paytoUri: string;

  /**
   * Did we previously complete a KYC process for this bank account?
   *
   * @deprecated no enough information since the kyc can be completed for one exchange but not for another
   * https://bugs.gnunet.org/view.php?id=9696
   */
  kycCompleted: boolean;

  /**
   * Currencies supported by the bank, if known.
   */
  currencies: string[] | undefined;

  label: string | undefined;
}

export interface ListBankAccountsResponse {
  accounts: WalletBankAccountInfo[];
}

export type GetBankAccountByIdResponse = WalletBankAccountInfo;

export interface GetBankAccountByIdRequest {
  bankAccountId: string;
}

export const codecForGetBankAccountByIdRequest =
  (): Codec<GetBankAccountByIdRequest> =>
    buildCodecForObject<GetBankAccountByIdRequest>()
      .property("bankAccountId", codecForString())
      .build("GetBankAccountByIdRequest");

/**
 * Wire fee for one wire method
 */
export interface WireFee {
  /**
   * Fee for wire transfers.
   */
  wireFee: AmountString;

  /**
   * Fees to close and refund a reserve.
   */
  closingFee: AmountString;

  /**
   * Start date of the fee.
   */
  startStamp: TalerProtocolTimestamp;

  /**
   * End date of the fee.
   */
  endStamp: TalerProtocolTimestamp;

  /**
   * Signature made by the exchange master key.
   */
  sig: string;
}

export type WireFeeMap = { [wireMethod: string]: WireFee[] };

export interface WireInfo {
  feesForType: WireFeeMap;
  accounts: ExchangeWireAccount[];
}

export interface ExchangeGlobalFees {
  startDate: TalerProtocolTimestamp;
  endDate: TalerProtocolTimestamp;

  historyFee: AmountString;
  accountFee: AmountString;
  purseFee: AmountString;

  historyTimeout: TalerProtocolDuration;
  purseTimeout: TalerProtocolDuration;

  purseLimit: number;

  signature: string;
}

export interface DenominationInfo {
  /**
   * Value of one coin of the denomination.
   */
  value: AmountString;

  /**
   * Hash of the denomination public key.
   * Stored in the database for faster lookups.
   */
  denomPubHash: string;

  denomPub: DenominationPubKey;

  /**
   * Fee for withdrawing.
   */
  feeWithdraw: AmountString;

  /**
   * Fee for depositing.
   */
  feeDeposit: AmountString;

  /**
   * Fee for refreshing.
   */
  feeRefresh: AmountString;

  /**
   * Fee for refunding.
   */
  feeRefund: AmountString;

  /**
   * Validity start date of the denomination.
   */
  stampStart: TalerProtocolTimestamp;

  /**
   * Date after which the currency can't be withdrawn anymore.
   */
  stampExpireWithdraw: TalerProtocolTimestamp;

  /**
   * Date after the denomination officially doesn't exist anymore.
   */
  stampExpireLegal: TalerProtocolTimestamp;

  /**
   * Data after which coins of this denomination can't be deposited anymore.
   */
  stampExpireDeposit: TalerProtocolTimestamp;

  exchangeBaseUrl: string;

  exchangeMasterPub: string;

  isLost: boolean;

  isOffered: boolean;

  masterSig: string;
}

export type DenomOperation = "deposit" | "withdraw" | "refresh" | "refund";
export type DenomOperationMap<T> = { [op in DenomOperation]: T };

export interface FeeDescription {
  group: string;
  from: AbsoluteTime;
  until: AbsoluteTime;
  fee?: AmountString;
}

export interface FeeDescriptionPair {
  group: string;
  from: AbsoluteTime;
  until: AbsoluteTime;
  left?: AmountString;
  right?: AmountString;
}

export interface TimePoint<T> {
  id: string;
  group: string;
  fee: AmountString;
  type: "start" | "end";
  moment: AbsoluteTime;
  denom: T;
}

export interface ExchangeFullDetails {
  exchangeBaseUrl: string;
  currency: string;
  paytoUris: string[];
  auditors: ExchangeAuditor[];
  wireInfo: WireInfo;
  denomFees: DenomOperationMap<FeeDescription[]>;
  transferFees: Record<string, FeeDescription[]>;
  globalFees: FeeDescription[];
}

export enum ExchangeTosStatus {
  Pending = "pending",
  Proposed = "proposed",
  Accepted = "accepted",
  MissingTos = "missing-tos",
}

export enum ExchangeEntryStatus {
  Preset = "preset",
  Ephemeral = "ephemeral",
  Used = "used",
}

/** How an exchange entry became known to the wallet. */
export enum ExchangeEntrySource {
  Builtin = "builtin",
  User = "user",
  Discovered = "discovered",
  Unknown = "unknown",
}

export const codecForExchangeEntryStatus = (): Codec<ExchangeEntryStatus> =>
  codecForEither(
    codecForConstString(ExchangeEntryStatus.Ephemeral),
    codecForConstString(ExchangeEntryStatus.Preset),
    codecForConstString(ExchangeEntryStatus.Used),
  );

export enum ExchangeUpdateStatus {
  Initial = "initial",
  InitialUpdate = "initial-update",
  Suspended = "suspended",
  UnavailableUpdate = "unavailable-update",
  Ready = "ready",
  ReadyUpdate = "ready-update",
  OutdatedUpdate = "outdated-update",
}

export enum ExchangeWalletKycStatus {
  Done = "done",
  /**
   * Wallet needs to request KYC status.
   */
  LegiInit = "legi-init",
  /**
   * User requires KYC or AML.
   */
  Legi = "legi",
}

export interface OperationErrorInfo {
  error: TalerErrorDetail;
}

/**
 * Info about an exchange entry in the wallet.
 */
/**
 * An exchange that changed its key set, pending the user's confirmation.
 *
 * The wallet has already adopted the new key set, so the entry works and the
 * older coins stay spendable.  What is withheld until this is confirmed is
 * withdrawing: the bank details a withdrawal pays into are signed by the
 * master key, so adopting a new one silently would let a URL that changed
 * hands redirect the next transfer.
 */
export interface ExchangeKeyChangeInfo {
  /** Master public key the exchange now uses, and the wallet now trusts. */
  currentMasterPub: string;
  currentCurrency: string;
  /** Master public key the wallet's older funds were issued under. */
  supersededMasterPub: string;
  supersededCurrency: string;
  /**
   * Whether the new key set still advertises denominations the wallet holds
   * coins of.
   *
   * False means the exchange does not offer to settle the older coins at all.
   * True is the exchange's claim that it does, not proof of continuity:
   * denomination public keys are public and anyone can re-publish them.
   */
  sharesDenominations: boolean;
  firstSeen: TalerPreciseTimestamp;
}

export interface ExchangeListItem {
  exchangeBaseUrl: string;
  source?: ExchangeEntrySource;
  masterPub: string | undefined;
  /**
   * Master public keys this exchange URL used before its current key.
   *
   * The array is sorted and does not include {@link masterPub}.
   */
  legacyMasterPubs: string[];
  /**
   * Set when the exchange changed its key set and the user has not confirmed
   * the change yet.  Withdrawals are refused while this is present.
   */
  unconfirmedKeyChange?: ExchangeKeyChangeInfo;
  currency: string;
  paytoUris: string[];
  tosStatus: ExchangeTosStatus;
  exchangeEntryStatus: ExchangeEntryStatus;
  exchangeUpdateStatus: ExchangeUpdateStatus;
  ageRestrictionOptions: number[];

  walletKycStatus?: ExchangeWalletKycStatus;
  walletKycReservePub?: string;
  walletKycAccessToken?: string;
  walletKycUrl?: string;

  /** Threshold that we've requested to satisfy. */
  walletKycRequestedThreshold?: string;

  /**
   * P2P payments are disabled with this exchange
   * (e.g. because no global fees are configured).
   */
  peerPaymentsDisabled: boolean;

  directDepositsDisabled: boolean;

  /** Set to true if this exchange doesn't charge any fees. */
  noFees: boolean;

  /** Most general scope that the exchange is a part of. */
  scopeInfo: ScopeInfo;

  /**
   * Instructs wallets to use certain bank-specific
   * language (for buttons) and/or other UI/UX customization
   * for compliance with the rules of that bank.
   */
  bankComplianceLanguage?: string;

  lastUpdateTimestamp: TalerPreciseTimestamp | undefined;

  /** Most recent successful withdrawal through this exchange. */
  lastWithdrawal?: TalerPreciseTimestamp;

  /**
   * Information about the last error that occurred when trying
   * to update the exchange info.
   */
  lastUpdateErrorInfo?: OperationErrorInfo;

  /**
   * Currency spec for the currency offered
   * by the exchange.
   */
  currencySpec: CurrencySpecification;
}

export interface ContactEntry {
  /**
   * Contact alias
   */
  alias: string;

  /**
   * Alias type
   */
  aliasType: string;

  /**
   * mailbox URI
   */
  mailboxBaseUri: string;

  /**
   * mailbox identity
   */
  mailboxAddress: HashCodeString;

  /**
   * The source of this contact
   * may be a URI
   */
  source: string;

  /**
   * The local petname of the contact
   */
  petname: string;
}

/**
 * Record metadata for mailbox messages
 */
export interface MailboxMessageRecord {
  // Origin mailbox
  originMailboxBaseUrl: string;

  // Time of download
  downloadedAt: Timestamp;

  // Taler URI in message
  talerUri: string;
}

export interface AcceptManualWithdrawalResult {
  /**
   * Transaction ID of the newly created withdrawal transaction.
   */
  transactionId: TransactionIdStr;

  /**
   * Public key of the newly created reserve.
   */
  reservePub: string;

  /**
   * Bank accounts of the exchange that can be used
   * to fund the withdrawal.
   */
  withdrawalAccountsList: WithdrawalExchangeAccountDetails[];
}

export interface WithdrawalDetailsForAmount {
  /**
   * Exchange base URL for the withdrawal.
   */
  exchangeBaseUrl: string;

  /**
   * Amount that the user will transfer to the exchange.
   */
  amountRaw: AmountString;

  /**
   * Amount that will be added to the user's wallet balance.
   */
  amountEffective: AmountString;

  /**
   * Number of coins that would be used for withdrawal.
   *
   * The UIs should warn if this number is too high (roughly at >100).
   */
  numCoins: number;

  /**
   * Ways to pay the exchange, including accounts that require currency conversion.
   */
  withdrawalAccountsList: WithdrawalExchangeAccountDetails[];

  /**
   * If the exchange supports age-restricted coins it will return
   * the array of ages.
   */
  ageRestrictionOptions?: number[];

  /**
   * Scope info of the currency withdrawn.
   */
  scopeInfo: ScopeInfo;

  /**
   * Set when the exchange changed its key set and the user has not confirmed
   * the change.  Accepting the withdrawal will be refused until they do, so
   * this is the point at which to warn them.
   */
  unconfirmedKeyChange?: ExchangeKeyChangeInfo;

  /**
   * KYC soft limit.
   *
   * Withdrawals over that amount will require KYC.
   */
  kycSoftLimit?: AmountString;

  /**
   * KYC soft limits.
   *
   * Withdrawals over that amount will be denied.
   */
  kycHardLimit?: AmountString;

  /**
   * Ways to pay the exchange.
   *
   * @deprecated in favor of withdrawalAccountsList
   */
  paytoUris: string[];
}

export interface DenomSelItem {
  denomPubHash: string;
  count: number;
  /**
   * Number of denoms/planchets to skip, because
   * a re-denomination effectively deleted them.
   *
   * For denom revocations, this equals count.
   * But for re-denominations to a smaller withdrawal
   * amounts, skip < count is possible.
   */
  skip?: number;
}

/**
 * Selected denominations with some extra info.
 */
export interface DenomSelectionState {
  totalCoinValue: AmountString;
  totalWithdrawCost: AmountString;
  selectedDenoms: DenomSelItem[];
  hasDenomWithAgeRestriction: boolean;
}

/**
 * Information about what will happen doing a withdrawal.
 *
 * Sent to the wallet frontend to be rendered and shown to the user.
 */
export interface ExchangeWithdrawalDetails {
  exchangePaytoUris: string[];

  /**
   * Set when the exchange changed its key set and the user has not confirmed
   * the change.  Copied onto the responses the UIs show before the user
   * commits to a withdrawal.
   */
  unconfirmedKeyChange?: ExchangeKeyChangeInfo;

  /**
   * Filtered wire info to send to the bank.
   */
  exchangeWireAccounts: string[];

  exchangeCreditAccountDetails: WithdrawalExchangeAccountDetails[];

  /**
   * Selected denominations for withdraw.
   */
  selectedDenoms: DenomSelectionState;

  /**
   * Amount that will be subtracted from the reserve's balance.
   */
  withdrawalAmountRaw: AmountString;

  /**
   * Amount that will actually be added to the wallet's balance.
   */
  withdrawalAmountEffective: AmountString;

  /**
   * If the exchange supports age-restricted coins it will return
   * the array of ages.
   *
   */
  ageRestrictionOptions?: number[];

  scopeInfo: ScopeInfo;

  /**
   * KYC soft limit.
   *
   * Withdrawals over that amount will require KYC.
   */
  kycSoftLimit?: AmountString;

  /**
   * KYC soft limits.
   *
   * Withdrawals over that amount will be denied.
   */
  kycHardLimit?: AmountString;
}

export interface GetExchangeTosResult {
  /**
   * Markdown version of the current ToS.
   */
  content: string;

  /**
   * Version tag of the current ToS.
   */
  currentEtag: string;

  /**
   * Version tag of the last ToS that the user has accepted,
   * if any.
   */
  acceptedEtag: string | undefined;

  /**
   * Accepted content type
   */
  contentType: string;

  /**
   * Language of the returned content.
   *
   * If missing, language is unknown.
   */
  contentLanguage: string | undefined;

  /**
   * Available languages as advertised by the exchange.
   */
  tosAvailableLanguages: string[];

  tosStatus: ExchangeTosStatus;
}

export interface TestPayArgs {
  merchantBaseUrl: string;
  merchantAuthToken?: string;
  amount: AmountString;
  summary: string;
  forcedCoinSel?: ForcedCoinSel;
}

export const codecForTestPayArgs = (): Codec<TestPayArgs> =>
  buildCodecForObject<TestPayArgs>()
    .property("merchantBaseUrl", codecForCanonBaseUrl())
    .property("merchantAuthToken", codecOptional(codecForString()))
    .property("amount", codecForAmountString())
    .property("summary", codecForString())
    .property("forcedCoinSel", codecForAny())
    .build("TestPayArgs");

export interface IntegrationTestArgs {
  exchangeBaseUrl: string;
  corebankApiBaseUrl: string;
  merchantBaseUrl: string;
  merchantAuthToken?: string;
  amountToWithdraw: AmountString;
  amountToSpend: AmountString;
}

export const codecForIntegrationTestArgs = (): Codec<IntegrationTestArgs> =>
  buildCodecForObject<IntegrationTestArgs>()
    .property("exchangeBaseUrl", codecForCanonBaseUrl())
    .property("merchantBaseUrl", codecForCanonBaseUrl())
    .property("merchantAuthToken", codecOptional(codecForString()))
    .property("amountToSpend", codecForAmountString())
    .property("amountToWithdraw", codecForAmountString())
    .property("corebankApiBaseUrl", codecForCanonBaseUrl())
    .build("IntegrationTestArgs");

export interface IntegrationTestV2Args {
  exchangeBaseUrl: string;
  corebankApiBaseUrl: string;
  merchantBaseUrl: string;
  merchantAuthToken?: string;
}

export const codecForIntegrationTestV2Args = (): Codec<IntegrationTestV2Args> =>
  buildCodecForObject<IntegrationTestV2Args>()
    .property("exchangeBaseUrl", codecForCanonBaseUrl())
    .property("merchantBaseUrl", codecForCanonBaseUrl())
    .property("merchantAuthToken", codecOptional(codecForString()))
    .property("corebankApiBaseUrl", codecForCanonBaseUrl())
    .build("IntegrationTestV2Args");

export interface GetExchangeEntryByUrlRequest {
  exchangeBaseUrl: string;
}

export interface GetExchangeDetailedInfoRequest {
  exchangeBaseUrl: string;
}

export const codecForGetExchangeEntryByUrlRequest =
  (): Codec<GetExchangeEntryByUrlRequest> =>
    buildCodecForObject<GetExchangeEntryByUrlRequest>()
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .build("GetExchangeEntryByUrlRequest");

export type GetExchangeEntryByUrlResponse = ExchangeListItem;

export interface AddExchangeRequest {
  /**
   * Either an http(s) exchange base URL or
   * a taler://add-exchange/ URI.
   */
  uri?: string;

  /**
   * Only ephemerally add the exchange.
   */
  ephemeral?: boolean;

  /**
   * Allow passing incomplete URLs.  The wallet will try to complete
   * the URL and throw an error if completion is not possible.
   */
  allowCompletion?: boolean;

  /**
   * @deprecated use a separate API call to start a forced exchange update instead
   */
  forceUpdate?: boolean;

  /**
   * @deprecated Use {@link uri} instead
   */
  exchangeBaseUrl?: string;

  progressToken?: string;
}

export interface AddExchangeResponse {
  /**
   * Base URL of the exchange that was added to the wallet.
   */
  exchangeBaseUrl: string;
}

export const codecForAddExchangeRequest = (): Codec<AddExchangeRequest> =>
  buildCodecForObject<AddExchangeRequest>()
    .property("allowCompletion", codecOptional(codecForBoolean()))
    .property("exchangeBaseUrl", codecOptional(codecForCanonBaseUrl()))
    .property("uri", codecOptional(codecForString()))
    .property("forceUpdate", codecOptional(codecForBoolean()))
    .property("ephemeral", codecOptional(codecForBoolean()))
    .property("progressToken", codecOptional(codecForString()))
    .build("AddExchangeRequest");

export interface UpdateExchangeEntryRequest {
  exchangeBaseUrl: string;
  force?: boolean;
}

export const codecForUpdateExchangeEntryRequest =
  (): Codec<UpdateExchangeEntryRequest> =>
    buildCodecForObject<UpdateExchangeEntryRequest>()
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .property("force", codecOptional(codecForBoolean()))
      .build("UpdateExchangeEntryRequest");

export interface GetExchangeResourcesRequest {
  exchangeBaseUrl: string;
}

export const codecForGetExchangeResourcesRequest =
  (): Codec<GetExchangeResourcesRequest> =>
    buildCodecForObject<GetExchangeResourcesRequest>()
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .build("GetExchangeResourcesRequest");

export interface GetExchangeResourcesResponse {
  hasResources: boolean;
}

export interface DeleteExchangeRequest {
  exchangeBaseUrl: string;

  /**
   * Delete the exchange even if it's in use.
   */
  purge?: boolean;
}

export const codecForDeleteExchangeRequest = (): Codec<DeleteExchangeRequest> =>
  buildCodecForObject<DeleteExchangeRequest>()
    .property("exchangeBaseUrl", codecForCanonBaseUrl())
    .property("purge", codecOptional(codecForBoolean()))
    .build("DeleteExchangeRequest");

export interface PurgeExchangeLegacyKeysRequest {
  exchangeBaseUrl: string;

  /**
   * Current master key observed by the client before confirming the purge.
   * The operation fails atomically if the exchange has changed keys since.
   */
  currentMasterPub: string;
}

export const codecForPurgeExchangeLegacyKeysRequest =
  (): Codec<PurgeExchangeLegacyKeysRequest> =>
    buildCodecForObject<PurgeExchangeLegacyKeysRequest>()
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .property("currentMasterPub", codecForString())
      .build("PurgeExchangeLegacyKeysRequest");

export interface GetExchangeTosRequest {
  exchangeBaseUrl: string;
  acceptedFormat?: string[];
  acceptLanguage?: string;
  progressToken?: string;
}

export const codecForGetExchangeTosRequest = (): Codec<GetExchangeTosRequest> =>
  buildCodecForObject<GetExchangeTosRequest>()
    .property("exchangeBaseUrl", codecForCanonBaseUrl())
    .property("acceptedFormat", codecOptional(codecForList(codecForString())))
    .property("acceptLanguage", codecOptional(codecForString()))
    .property("progressToken", codecOptional(codecForString()))
    .build("GetExchangeTosRequest");

export interface AcceptManualWithdrawalRequest {
  exchangeBaseUrl: string;
  amount: AmountString;
  restrictAge?: number;

  /**
   * Instead of generating a fresh, random reserve key pair,
   * use the provided reserve private key.
   *
   * Use with caution.  Usage of this field may be restricted
   * to developer mode.
   */
  forceReservePriv?: EddsaPrivateKeyString;

  progressToken?: string;
}

export const codecForAcceptManualWithdrawalRequest =
  (): Codec<AcceptManualWithdrawalRequest> =>
    buildCodecForObject<AcceptManualWithdrawalRequest>()
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .property("amount", codecForAmountString())
      .property("restrictAge", codecOptional(codecForNumber()))
      .property("forceReservePriv", codecOptional(codecForEddsaPrivateKey()))
      .property("progressToken", codecOptional(codecForString()))
      .build("AcceptManualWithdrawalRequest");

export interface GetWithdrawalDetailsForAmountRequest {
  exchangeBaseUrl?: string;

  /**
   * Specify currency scope for the withdrawal.
   *
   * May only be used when exchangeBaseUrl is not specified.
   */
  restrictScope?: ScopeInfo;

  amount: AmountString;

  restrictAge?: number;

  progressToken?: string;
}

export interface PrepareBankIntegratedWithdrawalRequest {
  talerWithdrawUri: string;

  progressToken?: string;
}

export const codecForPrepareBankIntegratedWithdrawalRequest =
  (): Codec<PrepareBankIntegratedWithdrawalRequest> =>
    buildCodecForObject<PrepareBankIntegratedWithdrawalRequest>()
      .property("talerWithdrawUri", codecForString())
      .property("progressToken", codecOptional(codecForString()))
      .build("PrepareBankIntegratedWithdrawalRequest");

export interface PrepareBankIntegratedWithdrawalResponse {
  transactionId: TransactionIdStr;
  info: WithdrawUriInfoResponse;
}

export interface ConfirmWithdrawalRequest {
  transactionId: string;
  exchangeBaseUrl: string;
  amount?: AmountString | undefined;
  forcedDenomSel?: ForcedDenomSel;
  restrictAge?: number;

  progressToken?: string;
}

export const codecForConfirmWithdrawalRequestRequest =
  (): Codec<ConfirmWithdrawalRequest> =>
    buildCodecForObject<ConfirmWithdrawalRequest>()
      .property("transactionId", codecForString())
      .property("amount", codecOptional(codecForAmountString()))
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .property("forcedDenomSel", codecForAny())
      .property("restrictAge", codecOptional(codecForNumber()))
      .property("progressToken", codecOptional(codecForString()))
      .build("ConfirmWithdrawalRequest");

export interface AcceptBankIntegratedWithdrawalRequest {
  talerWithdrawUri: string;
  exchangeBaseUrl: string;
  forcedDenomSel?: ForcedDenomSel;
  /**
   * Amount to withdraw.
   * If the bank's withdrawal operation uses a fixed amount,
   * this field must either be left undefined or its value must match
   * the amount from the withdrawal operation.
   */
  amount?: AmountString;
  restrictAge?: number;

  progressToken?: string;
}

export const codecForAcceptBankIntegratedWithdrawalRequest =
  (): Codec<AcceptBankIntegratedWithdrawalRequest> =>
    buildCodecForObject<AcceptBankIntegratedWithdrawalRequest>()
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .property("talerWithdrawUri", codecForString())
      .property("forcedDenomSel", codecForAny())
      .property("amount", codecOptional(codecForAmountString()))
      .property("restrictAge", codecOptional(codecForNumber()))
      .property("progressToken", codecOptional(codecForString()))
      .build("AcceptBankIntegratedWithdrawalRequest");

export const codecForGetWithdrawalDetailsForAmountRequest =
  (): Codec<GetWithdrawalDetailsForAmountRequest> =>
    buildCodecForObject<GetWithdrawalDetailsForAmountRequest>()
      .property("exchangeBaseUrl", codecOptional(codecForCanonBaseUrl()))
      .property("restrictScope", codecOptional(codecForScopeInfo()))
      .property("amount", codecForAmountString())
      .property("restrictAge", codecOptional(codecForNumber()))
      .property("progressToken", codecOptional(codecForString()))
      .build("GetWithdrawalDetailsForAmountRequest");

export interface AcceptExchangeTosRequest {
  exchangeBaseUrl: string;
}

export const codecForAcceptExchangeTosRequest =
  (): Codec<AcceptExchangeTosRequest> =>
    buildCodecForObject<AcceptExchangeTosRequest>()
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .build("AcceptExchangeTosRequest");

export interface ConfirmExchangeKeyChangeRequest {
  exchangeBaseUrl: string;
  /**
   * Master public key the exchange now uses.
   *
   * Required, so that a UI showing a stale key change cannot confirm a
   * different one than the user was looking at.
   */
  currentMasterPub: string;
}

export const codecForConfirmExchangeKeyChangeRequest =
  (): Codec<ConfirmExchangeKeyChangeRequest> =>
    buildCodecForObject<ConfirmExchangeKeyChangeRequest>()
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .property("currentMasterPub", codecForString())
      .build("ConfirmExchangeKeyChangeRequest");

export interface GetWithdrawalDetailsForUriRequest {
  talerWithdrawUri: string;
  /**
   * @deprecated not used
   */
  restrictAge?: number;

  progressToken?: string;
}

export const codecForGetWithdrawalDetailsForUri =
  (): Codec<GetWithdrawalDetailsForUriRequest> =>
    buildCodecForObject<GetWithdrawalDetailsForUriRequest>()
      .property("talerWithdrawUri", codecForString())
      .property("restrictAge", codecOptional(codecForNumber()))
      .property("progressToken", codecOptional(codecForString()))
      .build("GetWithdrawalDetailsForUriRequest");

export interface ListBankAccountsRequest {
  currency?: string;
}

export const codecForListBankAccounts = (): Codec<ListBankAccountsRequest> =>
  buildCodecForObject<ListBankAccountsRequest>()
    .property("currency", codecOptional(codecForString()))
    .build("ListBankAccountsRequest");

export interface AddBankAccountRequest {
  /**
   * Payto URI of the bank account that should be added.
   */
  paytoUri: string;

  /**
   * Human-readable label for the account.
   */
  label: string;

  /**
   * Currencies supported by the bank (if known).
   */
  currencies?: string[] | undefined;

  /**
   * Bank account that this new account should replace.
   */
  replaceBankAccountId?: string;
}

export interface AddBankAccountResponse {
  /**
   * Identifier of the added bank account.
   */
  bankAccountId: string;
}

export const codecForAddBankAccountRequest = (): Codec<AddBankAccountRequest> =>
  buildCodecForObject<AddBankAccountRequest>()
    .property("replaceBankAccountId", codecOptional(codecForString()))
    .property("paytoUri", codecForString())
    .property("label", codecForString())
    .property("currencies", codecOptional(codecForList(codecForString())))
    .build("AddBankAccountRequest");

export interface ForgetBankAccountRequest {
  bankAccountId: string;
}

export const codecForForgetBankAccount = (): Codec<ForgetBankAccountRequest> =>
  buildCodecForObject<ForgetBankAccountRequest>()
    .property("bankAccountId", codecForString())
    .build("ForgetBankAccountsRequest");

export interface PreparePayRequest {
  talerPayUri: string;
}

export const codecForPreparePayRequest = (): Codec<PreparePayRequest> =>
  buildCodecForObject<PreparePayRequest>()
    .property("talerPayUri", codecForString())
    .build("PreparePay");

export interface GetChoicesForPaymentRequest {
  transactionId: string;
  forcedCoinSel?: ForcedCoinSel;
}

export const codecForGetChoicesForPaymentRequest =
  (): Codec<GetChoicesForPaymentRequest> =>
    buildCodecForObject<GetChoicesForPaymentRequest>()
      .property("transactionId", codecForString())
      .property("forcedCoinSel", codecForAny())
      .build("GetChoicesForPaymentRequest");

export enum ChoiceSelectionDetailType {
  PaymentPossible = "payment-possible",
  InsufficientBalance = "insufficient-balance",
}

export type ChoiceSelectionDetail =
  | ChoiceSelectionDetailPaymentPossible
  | ChoiceSelectionDetailInsufficientBalance;

export interface ChoiceSelectionDetailPaymentPossible {
  status: ChoiceSelectionDetailType.PaymentPossible;
  amountRaw: AmountString;
  amountEffective: AmountString;
  scopeInfo: ScopeInfo | undefined;
  tokenDetails?: PaymentTokenAvailabilityDetails;
}

export interface ChoiceSelectionDetailInsufficientBalance {
  status: ChoiceSelectionDetailType.InsufficientBalance;
  amountRaw: AmountString;
  balanceDetails?: PaymentInsufficientBalanceDetails;
  tokenDetails?: PaymentTokenAvailabilityDetails;
}

export type GetChoicesForPaymentResult = {
  /**
   * Details for all choices in the contract.
   *
   * The index in this array corresponds to the choice
   * index in the original contract v1. For contract v0
   * orders, it will only contain a single choice with no
   * inputs/outputs.
   */
  choices: ChoiceSelectionDetail[];

  /**
   * Index of the choice in @e choices array to present
   * to the user as default.
   *
   * Won´t be set if no default selection is configured
   * or no choice is payable, otherwise, it will always
   * be 0 for v0 orders.
   */
  defaultChoiceIndex?: number;

  /**
   * Whether the choice referenced by @e automaticExecutableIndex
   * should be confirmed automatically without
   * user interaction.
   *
   * If true, the wallet should call `confirmPay'
   * immediately afterwards, if false, the user
   * should be first prompted to select and
   * confirm a choice. Undefined when no choices
   * are payable.
   */
  automaticExecution?: boolean;

  /**
   * Index of the choice that would be set to automatically
   * execute if the choice was payable. When @e automaticExecution
   * is set to true, the payment should be confirmed with this
   * choice index without user interaction.
   */
  automaticExecutableIndex?: number;

  /**
   * Data extracted from the contract terms that
   * is relevant for payment processing in the wallet.
   */
  contractTerms: MerchantContractTerms;
};

export interface UnclaimPaymentRequest {
  transactionId: TransactionIdStr;

  /** Enables progress correlation and cancellation through cancelProgressToken. */
  progressToken?: string;
}
export const codecForUnclaimPaymentRequest = (): Codec<UnclaimPaymentRequest> =>
  buildCodecForObject<UnclaimPaymentRequest>()
    .property("transactionId", codecForTransactionIdStr())
    .property("progressToken", codecOptional(codecForString()))
    .build("UnclaimPaymentRequest");

export interface UnclaimPaymentResult {
  talerPayUri: TalerUriString;
}

export interface ReclaimPaymentRequest {
  transactionId: TransactionIdStr;
}
export const codecForReclaimPaymentRequest = (): Codec<ReclaimPaymentRequest> =>
  buildCodecForObject<ReclaimPaymentRequest>()
    .property("transactionId", codecForTransactionIdStr())
    .build("ReclaimPaymentRequest");

export interface CheckPayTemplateRequest {
  talerPayTemplateUri: string;

  progressToken?: string;
}

export type CheckPayTemplateReponse = {
  templateDetails: WalletTemplateDetailsResponse;
  supportedCurrencies: string[];
};

export const codecForCheckPayTemplateRequest =
  (): Codec<CheckPayTemplateRequest> =>
    buildCodecForObject<CheckPayTemplateRequest>()
      .property("talerPayTemplateUri", codecForString())
      .property("progressToken", codecOptional(codecForString()))
      .build("CheckPayTemplateRequest");

export interface PreparePayTemplateRequest {
  talerPayTemplateUri: string;
  templateParams?: TemplateParams;

  progressToken?: string;
}

export const codecForPreparePayTemplateRequest =
  (): Codec<PreparePayTemplateRequest> =>
    buildCodecForObject<PreparePayTemplateRequest>()
      .property("talerPayTemplateUri", codecForString())
      .property("templateParams", codecForAny())
      .property("progressToken", codecOptional(codecForString()))
      .build("PreparePayTemplate");

export interface ConfirmPayRequest {
  transactionId: TransactionIdStr;
  useDonau?: boolean;
  sessionId?: string;
  forcedCoinSel?: ForcedCoinSel;

  /**
   * Whether token selection should be forced
   * e.g. use tokens with non-matching `expected_domains'
   *
   * Only applies to v1 orders.
   */
  forcedTokenSel?: boolean;

  /**
   * Only applies to v1 orders.
   */
  choiceIndex?: number;

  /**
   * Do not wait for the first payment success or error
   * before returning a response. Instead, status will
   * be communicated via notifications.
   *
   * Will become the default in future versions.
   */
  noWait?: boolean;
}

export const codecForConfirmPayRequest = (): Codec<ConfirmPayRequest> =>
  buildCodecForObject<ConfirmPayRequest>()
    .property("transactionId", codecForTransactionIdStr())
    .property("sessionId", codecOptional(codecForString()))
    .property("forcedCoinSel", codecForAny())
    .property("forcedTokenSel", codecOptional(codecForBoolean()))
    .property("choiceIndex", codecOptional(codecForNumber()))
    .property("useDonau", codecOptional(codecForBoolean()))
    .property("noWait", codecOptional(codecForBoolean()))
    .build("ConfirmPay");

export interface ListDiscountsRequest {
  /**
   * Filter by hash of token issue public key.
   */
  tokenIssuePubHash?: string;

  /**
   * Filter by merchant base URL.
   */
  merchantBaseUrl?: string;
}

export interface ListDiscountsResponse {
  discounts: DiscountListDetail[];
}

export interface DiscountListDetail {
  /**
   * Hash of token family info.
   */
  tokenFamilyHash: string;

  /**
   * Hash of token issue public key.
   */
  tokenIssuePubHash: string;

  /**
   * URL of the merchant issuing the token.
   */
  merchantBaseUrl: string;

  /**
   * Name of the merchant instance issuing the token.
   */
  merchantInfo?: MerchantInfo;

  /**
   * Human-readable name for the token family.
   */
  name: string;

  /**
   * Human-readable description for the token family.
   */
  description: string;

  /**
   * Optional map from IETF BCP 47 language tags to localized descriptions.
   */
  descriptionI18n: any | undefined;

  /**
   * Start time of the token's validity period.
   */
  validityStart: Timestamp;

  /**
   * End time of the token's validity period.
   */
  validityEnd: Timestamp;

  /**
   * Number of tokens available to use.
   */
  tokensAvailable: number;
}

export interface DeleteDiscountRequest {
  /**
   * Hash of token family info.
   */
  tokenFamilyHash: string;
}

export type ListSubscriptionsRequest = ListDiscountsRequest;

export interface ListSubscriptionsResponse {
  subscriptions: SubscriptionListDetail[];
}

export type SubscriptionListDetail = Omit<
  DiscountListDetail,
  "tokensAvailable"
>;

export interface DeleteSubscriptionRequest {
  /**
   * Hash of token family info.
   */
  tokenFamilyHash: string;
}

export const codecForListDiscountsRequest = (): Codec<ListDiscountsRequest> =>
  buildCodecForObject<ListDiscountsRequest>()
    .property("tokenIssuePubHash", codecOptional(codecForString()))
    .property("merchantBaseUrl", codecOptional(codecForCanonBaseUrl()))
    .build("ListDiscounts");

export const codecForDeleteDiscountRequest = (): Codec<DeleteDiscountRequest> =>
  buildCodecForObject<DeleteDiscountRequest>()
    .property("tokenFamilyHash", codecForString())
    .build("DeleteDiscount");

export const codecForListSubscriptionsRequest =
  (): Codec<ListSubscriptionsRequest> =>
    buildCodecForObject<ListSubscriptionsRequest>()
      .property("tokenIssuePubHash", codecOptional(codecForString()))
      .property("merchantBaseUrl", codecOptional(codecForCanonBaseUrl()))
      .build("ListSubscriptions");

export const codecForDeleteSubscriptionRequest =
  (): Codec<DeleteSubscriptionRequest> =>
    buildCodecForObject<DeleteSubscriptionRequest>()
      .property("tokenFamilyHash", codecForString())
      .build("DeleteSubscription");

export interface CoreApiRequestEnvelope {
  id: string;
  operation: string;
  args: unknown;
}

export type CoreApiResponse = CoreApiResponseSuccess | CoreApiResponseError;

export type CoreApiMessageEnvelope = CoreApiResponse | CoreApiNotification;

export interface CoreApiNotification {
  type: "notification";
  payload: unknown;
}

export interface CoreApiResponseSuccess {
  // To distinguish the message from notifications
  type: "response";
  operation: string;
  id: string;
  result: unknown;
}

export interface CoreApiResponseError {
  // To distinguish the message from notifications
  type: "error";
  operation: string;
  id: string;
  error: TalerErrorDetail;
}

export interface WithdrawTestBalanceRequest {
  /**
   * Amount to withdraw.
   */
  amount: AmountString;

  /**
   * Corebank API base URL.
   */
  corebankApiBaseUrl: string;

  /**
   * Exchange to use for withdrawal.
   */
  exchangeBaseUrl: string;

  /**
   * Force the usage of a particular denomination selection.
   *
   * Only useful for testing.
   */
  forcedDenomSel?: ForcedDenomSel;

  /**
   * If set to true, treat the account created during
   * the withdrawal as a foreign withdrawal account.
   */
  useForeignAccount?: boolean;
}

/**
 * Request to the crypto worker to make a sync signature.
 */
export interface MakeSyncSignatureRequest {
  accountPriv: string;
  oldHash: string | undefined;
  newHash: string;
}

/**
 * Planchet for a coin during refresh.
 */
export interface RefreshPlanchetInfo {
  /**
   * Public key for the coin.
   */
  coinPub: string;

  /**
   * Private key for the coin.
   */
  coinPriv: string;

  /**
   * Blinded public key.
   */
  coinEv: CoinEnvelope;

  coinEvHash: string;

  /**
   * Blinding key used.
   */
  blindingKey: string;

  maxAge: number;

  ageCommitmentProof?: AgeCommitmentProof;

  exchangeWithdrawValues: ExchangeWithdrawValue;
}

export const codecForWithdrawTestBalance =
  (): Codec<WithdrawTestBalanceRequest> =>
    buildCodecForObject<WithdrawTestBalanceRequest>()
      .property("amount", codecForAmountString())
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .property("forcedDenomSel", codecForAny())
      .property("corebankApiBaseUrl", codecForCanonBaseUrl())
      .property("useForeignAccount", codecOptional(codecForBoolean()))
      .build("WithdrawTestBalanceRequest");

export interface SetCoinSuspendedRequest {
  coinPub: string;
  suspended: boolean;
}

export const codecForSetCoinSuspendedRequest =
  (): Codec<SetCoinSuspendedRequest> =>
    buildCodecForObject<SetCoinSuspendedRequest>()
      .property("coinPub", codecForString())
      .property("suspended", codecForBoolean())
      .build("SetCoinSuspendedRequest");

export interface RefreshCoinSpec {
  coinPub: string;
  amount?: AmountString;
}

export const codecForRefreshCoinSpec = (): Codec<RefreshCoinSpec> =>
  buildCodecForObject<RefreshCoinSpec>()
    .property("amount", codecOptional(codecForAmountString()))
    .property("coinPub", codecForString())
    .build("ForceRefreshRequest");

export interface ForceRefreshRequest {
  refreshCoinSpecs: RefreshCoinSpec[];
}

export interface ForceRefreshResponse {
  refreshGroupId: string;
}

export const codecForForceRefreshRequest = (): Codec<ForceRefreshRequest> =>
  buildCodecForObject<ForceRefreshRequest>()
    .property("refreshCoinSpecs", codecForList(codecForRefreshCoinSpec()))
    .build("ForceRefreshRequest");

export interface PrepareRefundRequest {
  talerRefundUri: string;
}

export interface StartRefundQueryForUriResponse {
  /**
   * Transaction id of the *payment* where the refund query was started.
   */
  transactionId: TransactionIdStr;
}

export const codecForPrepareRefundRequest = (): Codec<PrepareRefundRequest> =>
  buildCodecForObject<PrepareRefundRequest>()
    .property("talerRefundUri", codecForString())
    .build("PrepareRefundRequest");

export interface StartRefundQueryRequest {
  transactionId: TransactionIdStr;
}

export const codecForStartRefundQueryRequest =
  (): Codec<StartRefundQueryRequest> =>
    buildCodecForObject<StartRefundQueryRequest>()
      .property("transactionId", codecForTransactionIdStr())
      .build("StartRefundQueryRequest");

export interface FailTransactionRequest {
  transactionId: TransactionIdStr;
}

export const codecForFailTransactionRequest =
  (): Codec<FailTransactionRequest> =>
    buildCodecForObject<FailTransactionRequest>()
      .property("transactionId", codecForTransactionIdStr())
      .build("FailTransactionRequest");

export interface SuspendTransactionRequest {
  transactionId: TransactionIdStr;
}

export const codecForSuspendTransaction =
  (): Codec<SuspendTransactionRequest> =>
    buildCodecForObject<AbortTransactionRequest>()
      .property("transactionId", codecForTransactionIdStr())
      .build("SuspendTransactionRequest");

export interface ResumeTransactionRequest {
  transactionId: TransactionIdStr;
}

export const codecForResumeTransaction = (): Codec<ResumeTransactionRequest> =>
  buildCodecForObject<ResumeTransactionRequest>()
    .property("transactionId", codecForTransactionIdStr())
    .build("ResumeTransactionRequest");

export interface AbortTransactionRequest {
  transactionId: TransactionIdStr;
}

export interface FailTransactionRequest {
  transactionId: TransactionIdStr;
}

export const codecForAbortTransaction = (): Codec<AbortTransactionRequest> =>
  buildCodecForObject<AbortTransactionRequest>()
    .property("transactionId", codecForTransactionIdStr())
    .build("AbortTransactionRequest");

export interface DepositGroupFees {
  coin: AmountString;
  wire: AmountString;
  refresh: AmountString;
}

export interface CreateDepositGroupRequest {
  depositPaytoUri: string;

  /**
   * Instructed amount used for deposit coin selection.
   */
  amount: AmountString;

  /**
   * Restrict the deposit to a certain scope.
   */
  restrictScope?: ScopeInfo;

  /**
   * Use a fixed merchant private key.
   */
  testingFixedPriv?: string;

  /**
   * Optional wire deadline for the deposits.
   */
  wireDeadline?: TalerProtocolTimestamp;

  /**
   * Pre-allocated transaction ID.
   * Allows clients to easily handle notifications
   * that occur while the operation has been created but
   * before the creation request has returned.
   */
  transactionId?: TransactionIdStr;

  progressToken?: string;
}

export interface CheckDepositRequest {
  /**
   * Payto URI to identify the (bank) account that the exchange will wire
   * the money to.
   */
  depositPaytoUri: string;

  /**
   * Instructed amount used for deposit coin selection.
   *
   * CheckDepositResponse reports the resulting wallet cost and destination
   * amount, which can differ because of fees and refresh change.
   */
  amount: AmountString;

  /**
   * Restrict the deposit to a certain scope.
   */
  restrictScope?: ScopeInfo;

  progressToken?: string;
}

export const codecForCheckDepositRequest = (): Codec<CheckDepositRequest> =>
  buildCodecForObject<CheckDepositRequest>()
    .property("restrictScope", codecOptional(codecForScopeInfo()))
    .property("amount", codecForAmountString())
    .property("depositPaytoUri", codecForString())
    .property("progressToken", codecOptional(codecForString()))
    .build("CheckDepositRequest");

export interface CheckDepositResponse {
  totalDepositCost: AmountString;
  effectiveDepositAmount: AmountString;
  fees: DepositGroupFees;

  kycSoftLimit?: AmountString;
  kycHardLimit?: AmountString;

  /**
   * Base URL of exchanges that would likely require soft KYC.
   */
  kycExchanges?: string[];
}

export const codecForCreateDepositGroupRequest =
  (): Codec<CreateDepositGroupRequest> =>
    buildCodecForObject<CreateDepositGroupRequest>()
      .property("restrictScope", codecOptional(codecForScopeInfo()))
      .property("amount", codecForAmountString())
      .property("depositPaytoUri", codecForString())
      .property("transactionId", codecOptional(codecForTransactionIdStr()))
      .property("testingFixedPriv", codecOptional(codecForString()))
      .property("wireDeadline", codecOptional(codecForTimestamp))
      .property("progressToken", codecOptional(codecForString()))
      .build("CreateDepositGroupRequest");

/**
 * Response to a createDepositGroup request.
 */
export interface CreateDepositGroupResponse {
  /**
   * Transaction ID of the newly created deposit transaction.
   */
  transactionId: TransactionIdStr;

  /**
   * Current state of the new deposit transaction.
   * Returned as a performance optimization, so that the UI
   * doesn't have to do a separate getTransactionById.
   */
  txState: TransactionState;

  /**
   * @deprecated 2025-06-03, use transactionId instead.
   */
  depositGroupId: string;
}

export interface WithdrawUriInfoResponse {
  operationId: string;
  status: WithdrawalOperationStatusFlag;
  confirmTransferUrl?: string;
  currency: string;
  amount: AmountString | undefined;

  /**
   * Set to true if the user is allowed to edit the amount.
   *
   * Note that even with a non-editable amount, the amount
   * might be undefined at the beginning of the withdrawal
   * process.
   */
  editableAmount: boolean;
  maxAmount: AmountString | undefined;
  wireFee: AmountString | undefined;
  defaultExchangeBaseUrl?: string;
  editableExchange: boolean;
  possibleExchanges: ExchangeListItem[];
}

export interface DeleteTransactionRequest {
  transactionId: TransactionIdStr;
}

export interface RetryTransactionRequest {
  transactionId: TransactionIdStr;
}

export const codecForDeleteTransactionRequest =
  (): Codec<DeleteTransactionRequest> =>
    buildCodecForObject<DeleteTransactionRequest>()
      .property("transactionId", codecForTransactionIdStr())
      .build("DeleteTransactionRequest");

export const codecForRetryTransactionRequest =
  (): Codec<RetryTransactionRequest> =>
    buildCodecForObject<RetryTransactionRequest>()
      .property("transactionId", codecForTransactionIdStr())
      .build("RetryTransactionRequest");

export interface ActiveTask {
  taskId: string;
  transaction?: TransactionIdStr | undefined;
  firstTry?: AbsoluteTime | undefined;
  nextTry?: AbsoluteTime | undefined;
  retryCounter?: number | undefined;
  lastError?: TalerErrorDetail | undefined;
}

export interface GetActiveTasksResponse {
  tasks: ActiveTask[];
}

export interface ImportDbRequest {
  dump?: any;

  /** Correlates progress notifications and allows cancellation. */
  progressToken?: string;
}

export const codecForImportDbRequest = (): Codec<ImportDbRequest> =>
  buildCodecForObject<ImportDbRequest>()
    .property("dump", codecForAny())
    .property("progressToken", codecOptional(codecForString()))
    .build("ImportDbRequest");

export interface ForcedDenomSel {
  denoms: {
    value: AmountString;
    count: number;
  }[];
}

/**
 * Forced coin selection for deposits/payments.
 */
export interface ForcedCoinSel {
  coins: {
    value: AmountString;
    contribution: AmountString;
  }[];
}

export interface TestPayResult {
  /**
   * Number of coins used for the payment.
   */
  numCoins: number;
}

export interface SelectedCoin {
  denomPubHash: string;
  coinPub: string;
  contribution: AmountString;
  exchangeBaseUrl: string;
  /** Master public key that signed the denomination. */
  exchangeMasterPub: string;
}

export interface SelectedProspectiveCoin {
  denomPubHash: string;
  contribution: AmountString;
  exchangeBaseUrl: string;
  /** Master public key that signed the denomination. */
  exchangeMasterPub: string;
}

/**
 * Result of selecting coins, contains the exchange, and selected
 * coins with their denomination.
 */
export interface PayCoinSelection {
  coins: SelectedCoin[];

  /**
   * How much of the wire fees is the customer paying?
   */
  customerWireFees: AmountString;

  /**
   * How much of the deposit fees is the customer paying?
   */
  customerDepositFees: AmountString;

  /**
   * How much of the deposit fees does the exchange charge in total?
   */
  totalDepositFees: AmountString;
}

export interface CheckPeerPushDebitRequest {
  /**
   * Preferred exchange to use for the p2p payment.
   */
  exchangeBaseUrl?: string;

  /**
   * Instructed amount.
   *
   * FIXME: Allow specifying the instructed amount type.
   */
  amount: AmountString;

  /**
   * Restrict the scope of funds that can be spent via the given
   * scope info.
   */
  restrictScope?: ScopeInfo;

  progressToken?: string;
}

export const codecForCheckPeerPushDebitRequest =
  (): Codec<CheckPeerPushDebitRequest> =>
    buildCodecForObject<CheckPeerPushDebitRequest>()
      .property("exchangeBaseUrl", codecOptional(codecForCanonBaseUrl()))
      .property("restrictScope", codecOptional(codecForScopeInfo()))
      .property("amount", codecForAmountString())
      .property("progressToken", codecOptional(codecForString()))
      .build("CheckPeerPushDebitRequest");

export type CheckPeerPushDebitResponse =
  | CheckPeerPushDebitOkResponse
  | CheckPeerPushDebitInsufficientBalanceResponse;

export interface CheckPeerPushDebitInsufficientBalanceResponse {
  type: "insufficient-balance";

  insufficientBalanceDetails: PaymentInsufficientBalanceDetails;
}

export interface CheckPeerPushDebitOkResponse {
  type: "ok";

  amountRaw: AmountString;

  amountEffective: AmountString;

  /**
   * Exchange base URL.
   */
  exchangeBaseUrl: string;

  /**
   * Maximum expiration date, based on how close the coins
   * used for the payment are to expiry.
   *
   * The value is based on when the wallet would typically
   * automatically refresh the coins on its own, leaving enough
   * time to get a refund for the push payment and refresh the
   * coin.
   */
  maxExpirationDate: TalerProtocolTimestamp;

  /**
   * Default expiration, as given by the exchange
   * (or 1 week if the exchange does not specify it).
   */
  defaultExpiration: TalerProtocolDuration;

  /**
   * Opaque description of the values reviewed by the caller.  Passing this
   * back to initiatePeerPushDebit makes wallet-core reject the operation when
   * coin selection, fees or the selected exchange changed in the meantime.
   *
   * Optional for compatibility with older wallet-core implementations.
   */
  peerPushDebitQuote?: string;
}

export interface InitiatePeerPushDebitRequest {
  exchangeBaseUrl?: string;

  /**
   * Restrict the scope of funds that can be spent via the given
   * scope info.
   */
  restrictScope?: ScopeInfo;

  /** Quote returned by checkPeerPushDebitV2. */
  peerPushDebitQuote?: string;

  partialContractTerms: PartialPeerContractTerms;
}

export interface InitiatePeerPushDebitResponse {
  exchangeBaseUrl: string;
  pursePub: string;
  mergePriv: string;
  contractPriv: string;
  transactionId: TransactionIdStr;
}

export const codecForInitiatePeerPushDebitRequest =
  (): Codec<InitiatePeerPushDebitRequest> =>
    buildCodecForObject<InitiatePeerPushDebitRequest>()
      .property("partialContractTerms", codecForPartialPeerContractTerms())
      .property("exchangeBaseUrl", codecOptional(codecForCanonBaseUrl()))
      .property("restrictScope", codecOptional(codecForScopeInfo()))
      .property("peerPushDebitQuote", codecOptional(codecForString()))
      .build("InitiatePeerPushDebitRequest");

/**
 * Result of initiating a peer-push-credit payment.
 *
 * Either {@link talerUri} or {@link transactionId} must be specified.
 */
export interface PreparePeerPushCreditRequest {
  talerUri?: string;
  transactionId?: string;

  progressToken?: string;
}

/**
 * Result of initiating a peer-pull-debit payment.
 *
 * Either {@link talerUri} or {@link transactionId} must be specified.
 */
export interface PreparePeerPullDebitRequest {
  talerUri?: string;
  transactionId?: string;

  progressToken?: string;
}

export interface PreparePeerPushCreditResponse {
  contractTerms: PeerContractTerms;
  amountRaw: AmountString;
  amountEffective: AmountString;

  transactionId: TransactionIdStr;

  /**
   * State of the existing or newly created transaction.
   */
  txState: TransactionState;

  exchangeBaseUrl: string;

  scopeInfo: ScopeInfo;

  /**
   * @deprecated
   */
  amount: AmountString;
}

export interface PreparePeerPullDebitResponse {
  contractTerms: PeerContractTerms;

  amountRaw: AmountString;
  amountEffective: AmountString;

  transactionId: TransactionIdStr;

  /**
   * State of the existing or newly created transaction.
   */
  txState: TransactionState;

  exchangeBaseUrl: string;

  scopeInfo: ScopeInfo;

  /**
   * @deprecated Redundant field with bad name, will be removed soon.
   */
  amount: AmountString;
}

export const codecForPreparePeerPushCreditRequest =
  (): Codec<PreparePeerPushCreditRequest> =>
    buildCodecForObject<PreparePeerPushCreditRequest>()
      .property("talerUri", codecOptional(codecForString()))
      .property("transactionId", codecOptional(codecForString()))
      .property("progressToken", codecOptional(codecForString()))
      .build("CheckPeerPushPaymentRequest");

export const codecForCheckPeerPullPaymentRequest =
  (): Codec<PreparePeerPullDebitRequest> =>
    buildCodecForObject<PreparePeerPullDebitRequest>()
      .property("talerUri", codecOptional(codecForString()))
      .property("transactionId", codecOptional(codecForString()))
      .property("progressToken", codecOptional(codecForString()))
      .build("PreparePeerPullDebitRequest");

export interface ConfirmPeerPushCreditRequest {
  transactionId: string;

  progressToken?: string;
}
export interface AcceptPeerPushPaymentResponse {
  transactionId: TransactionIdStr;
}

export interface AcceptPeerPullPaymentResponse {
  transactionId: TransactionIdStr;
}

export const codecForConfirmPeerPushPaymentRequest =
  (): Codec<ConfirmPeerPushCreditRequest> =>
    buildCodecForObject<ConfirmPeerPushCreditRequest>()
      .property("transactionId", codecForString())
      .property("progressToken", codecOptional(codecForString()))
      .build("ConfirmPeerPushCreditRequest");

export interface ConfirmPeerPullDebitRequest {
  transactionId: TransactionIdStr;
}

export interface ApplyDevExperimentRequest {
  devExperimentUri: string;
}

export const codecForApplyDevExperiment =
  (): Codec<ApplyDevExperimentRequest> =>
    buildCodecForObject<ApplyDevExperimentRequest>()
      .property("devExperimentUri", codecForString())
      .build("ApplyDevExperimentRequest");

export const codecForAcceptPeerPullPaymentRequest =
  (): Codec<ConfirmPeerPullDebitRequest> =>
    buildCodecForObject<ConfirmPeerPullDebitRequest>()
      .property("transactionId", codecForTransactionIdStr())
      .build("ConfirmPeerPullDebitRequest");

export interface CheckPeerPullCreditRequest {
  /**
   * Require using this particular exchange for this operation.
   */
  exchangeBaseUrl?: string;

  restrictScope?: ScopeInfo;

  amount: AmountString;

  progressToken?: string;
}

export const codecForPreparePeerPullPaymentRequest =
  (): Codec<CheckPeerPullCreditRequest> =>
    buildCodecForObject<CheckPeerPullCreditRequest>()
      .property("amount", codecForAmountString())
      .property("exchangeBaseUrl", codecOptional(codecForCanonBaseUrl()))
      .property("restrictScope", codecOptional(codecForScopeInfo()))
      .property("progressToken", codecOptional(codecForString()))
      .build("CheckPeerPullCreditRequest");

export interface CheckPeerPullCreditResponse {
  exchangeBaseUrl: string;
  amountRaw: AmountString;
  amountEffective: AmountString;

  /**
   * Wallet-selected default expiration for the request.
   */
  defaultExpiration: TalerProtocolDuration;

  /**
   * Number of coins that will be used,
   * can be used by the UI to warn if excessively large.
   */
  numCoins: number;
}

export interface InitiatePeerPullCreditRequest {
  exchangeBaseUrl?: string;
  partialContractTerms: PeerContractTerms;

  progressToken?: string;
}

export const codecForInitiatePeerPullPaymentRequest =
  (): Codec<InitiatePeerPullCreditRequest> =>
    buildCodecForObject<InitiatePeerPullCreditRequest>()
      .property("partialContractTerms", codecForPeerContractTerms())
      .property("exchangeBaseUrl", codecOptional(codecForCanonBaseUrl()))
      .property("progressToken", codecOptional(codecForString()))
      .build("InitiatePeerPullCreditRequest");

export interface InitiatePeerPullCreditResponse {
  /**
   * Taler URI for the other party to make the payment
   * that was requested.
   *
   * @deprecated since it's not necessarily valid yet until the tx is in the right state
   */
  talerUri: string;

  transactionId: TransactionIdStr;
}

export interface CanonicalizeBaseUrlRequest {
  url: string;
}

export const codecForCanonicalizeBaseUrlRequest =
  (): Codec<CanonicalizeBaseUrlRequest> =>
    buildCodecForObject<CanonicalizeBaseUrlRequest>()
      .property("url", codecForString())
      .build("CanonicalizeBaseUrlRequest");

export interface CanonicalizeBaseUrlResponse {
  url: string;
}

export interface ValidateIbanRequest {
  iban: string;
}

export const codecForValidateIbanRequest = (): Codec<ValidateIbanRequest> =>
  buildCodecForObject<ValidateIbanRequest>()
    .property("iban", codecForString())
    .build("ValidateIbanRequest");

export interface ValidateIbanResponse {
  valid: boolean;
}

export type TransactionStateFilter = "nonfinal";

export interface TestingSetTimetravelRequest {
  offsetMs: number;
}

export const codecForTestingSetTimetravelRequest =
  (): Codec<TestingSetTimetravelRequest> =>
    buildCodecForObject<TestingSetTimetravelRequest>()
      .property("offsetMs", codecForNumber())
      .build("TestingSetTimetravelRequest");

export interface AllowedAuditorInfo {
  auditorBaseUrl: string;
  auditorPub: string;
}

export interface AllowedExchangeInfo {
  exchangeBaseUrl: string;
  exchangePub: string;
}

/**
 * Data extracted from the contract terms that is relevant for payment
 * processing in the wallet.
 */
export interface DownloadedContractData {
  contractTermsRaw: any;
  contractTerms: MerchantContractTerms;
  contractTermsHash: HashCode;
}

export type PayWalletData = {
  choice_index?: number;
  tokens_evs: TokenEnvelope[];

  // Request for donation receipts to be issued.
  // @since protocol **v21**
  donau?: DonationRequestData;
};

export interface DonationRequestData {
  // Base URL of the selected Donau
  url: string;

  // Year for which the donation receipts are expected.
  // Also determines which keys are used to sign the
  // blinded donation receipts.
  year: number;

  // Array of blinded donation receipts to sign.
  // Must NOT be empty (if no donation receipts
  // are desired, just leave the entire donau
  // argument blank).
  budikeypairs: BlindedDonationReceiptKeyPair[];
}

export interface TestingWaitExchangeStateRequest {
  exchangeBaseUrl: string;
  walletKycStatus?: ExchangeWalletKycStatus;
}

export interface TestingWaitExchangeReadyRequest {
  exchangeBaseUrl: string;
  /**
   * Do not stop waiting even when the exchange is
   * in an error state.
   */
  noBail?: boolean;
  /**
   * Force waiting until an update really happened.
   */
  forceUpdate?: boolean;
  /**
   * Only consider the exchange as ready if the
   * next auto-refresh is scheduled for the future.
   */
  waitAutoRefresh?: boolean;
}

export const codecForTestingWaitExchangeReadyRequest =
  (): Codec<TestingWaitExchangeReadyRequest> =>
    buildCodecForObject<TestingWaitExchangeReadyRequest>()
      .property("exchangeBaseUrl", codecForString())
      .property("noBail", codecOptional(codecForBoolean()))
      .property("forceUpdate", codecOptional(codecForBoolean()))
      .property("waitAutoRefresh", codecOptional(codecForBoolean()))
      .build("TestingWaitExchangeReadyRequest");

export interface TransactionStatePattern {
  major: TransactionMajorState | TransactionStateWildcard;
  minor?: TransactionMinorState | TransactionStateWildcard;

  /**
   * Required value of the "working" flag of the transaction state.
   * A transaction state without the flag counts as false.
   *
   * If left undefined, the flag is not taken into account
   * when matching, i.e. it behaves like a wildcard.
   */
  working?: boolean | TransactionStateWildcard;
}

/**
 * Does the transaction state match the pattern?
 *
 * Beware that a pattern without a minor state only matches states
 * that have no minor state, whereas a pattern without the working
 * flag matches regardless of the flag.
 */
export function matchTransactionState(
  st: TransactionState,
  pat: TransactionStatePattern,
): boolean {
  return (
    (pat.major === "*" || st.major === pat.major) &&
    (pat.minor === "*" || st.minor === pat.minor) &&
    (pat.working === undefined ||
      pat.working === "*" ||
      pat.working === !!st.working)
  );
}

export interface TestingWaitBalanceRequest {
  type: "material" | "available";
  amount: AmountString;
}

// No validation, test-only request.
export const codecForTestingWaitBalanceRequest: () => Codec<TestingWaitBalanceRequest> =
  codecForAny;

export interface TestingWaitTransactionRequest {
  transactionId: TransactionIdStr;

  /**
   * Additional identifier that is used in the logs
   * to easily find the status of the particular wait
   * request.
   */
  logId?: string;

  /**
   * After the timeout has passed, give up on
   * waiting for the desired state and raise
   * an error instead.
   */
  timeout?: DurationUnitSpec;

  /**
   * If set to true, wait until the desired state
   * is reached with an error.
   */
  requireError?: boolean;

  txState: TestingWaitTxStateSpec;

  /**
   * States that end the wait even though they are not the
   * state that was waited for.  The response says which of the
   * two sets matched.
   *
   * Without this, a transaction that ends up in a state it will
   * never leave keeps the caller waiting until the timeout.
   */
  bailStates?: TransactionStatePattern[];

  /**
   * End the wait as soon as an error is recorded for the transaction.
   *
   * Beware that this includes transient errors of retried operations,
   * which are cleared again once the operation succeeds.
   */
  bailOnError?: boolean;
}

/**
 * State(s) to wait for.
 *
 * A plain pattern or a list of patterns (matching any of them),
 * a wallet-internal numeric state ID, or one of the shorthands
 * for a category of states.
 */
export type TestingWaitTxStateSpec =
  | TransactionStatePattern
  | TransactionStatePattern[]
  | number
  | "nonpending"
  | "final";

export interface TestingWaitTransactionStateResponse {
  /**
   * Which set of states ended the wait: the requested state
   * or one of the bail states.
   */
  matched: "target" | "bail";

  /**
   * State that ended the wait.
   */
  txState: TransactionState;

  /**
   * Wallet-internal state ID, only used for debugging and testing.
   */
  stId: number;
}

const codecForDurationUnitSpec = (): Codec<DurationUnitSpec> =>
  buildCodecForObject<DurationUnitSpec>()
    .property("seconds", codecOptional(codecForNumber()))
    .property("minutes", codecOptional(codecForNumber()))
    .property("hours", codecOptional(codecForNumber()))
    .property("days", codecOptional(codecForNumber()))
    .property("months", codecOptional(codecForNumber()))
    .property("years", codecOptional(codecForNumber()))
    .build("DurationUnitSpec");

export const codecForTransactionStatePattern =
  (): Codec<TransactionStatePattern> =>
    buildCodecForObject<TransactionStatePattern>()
      .property(
        "major",
        codecForEither(
          codecForStringEnum(TransactionMajorState),
          codecForConstString("*"),
        ),
      )
      .property(
        "minor",
        codecOptional(
          codecForEither(
            codecForStringEnum(TransactionMinorState),
            codecForConstString("*"),
          ),
        ),
      )
      .property(
        "working",
        codecOptional(
          codecForEither(codecForBoolean(), codecForConstString("*")),
        ),
      )
      .build("TransactionStatePattern");

const codecForTestingWaitTxStateSpec = (): Codec<TestingWaitTxStateSpec> =>
  codecForEither(
    codecForConstString("nonpending"),
    codecForConstString("final"),
    codecForNumber(),
    codecForTransactionStatePattern(),
    codecForList(codecForTransactionStatePattern()),
  );

export const codecForTestingWaitTransactionRequest =
  (): Codec<TestingWaitTransactionRequest> =>
    buildCodecForObject<TestingWaitTransactionRequest>()
      .property("transactionId", codecForTransactionIdStr())
      .property("logId", codecOptional(codecForString()))
      .property("timeout", codecOptional(codecForDurationUnitSpec()))
      .property("requireError", codecOptional(codecForBoolean()))
      .property("txState", codecForTestingWaitTxStateSpec())
      .property(
        "bailStates",
        codecOptional(codecForList(codecForTransactionStatePattern())),
      )
      .property("bailOnError", codecOptional(codecForBoolean()))
      .build("TestingWaitTransactionRequest");

export interface TestingGetReserveHistoryRequest {
  reservePub: string;
  exchangeBaseUrl: string;
}

export const codecForTestingGetReserveHistoryRequest =
  (): Codec<TestingGetReserveHistoryRequest> =>
    buildCodecForObject<TestingGetReserveHistoryRequest>()
      .property("reservePub", codecForString())
      .property("exchangeBaseUrl", codecForString())
      .build("TestingGetReserveHistoryRequest");

export interface TestingGetDenomStatsRequest {
  exchangeBaseUrl: string;
}

export interface TestingGetDenomStatsResponse {
  numKnown: number;
  numOffered: number;
  numLost: number;
}

export type DiagnosticsFormat = "json" | "yaml";

export interface DiagnosticsFrontendInfo {
  name: string;
  version: string;
  platform?: string;
}

export interface GetDiagnosticsRequest {
  /** Serialized output format.  Defaults to JSON. */
  format?: DiagnosticsFormat;

  /** Maximum number of newest transactions to include.  Defaults to 100. */
  transactionLimit?: number;

  /** Information supplied by the wallet frontend invoking wallet-core. */
  frontendInfo?: DiagnosticsFrontendInfo;
}

/**
 * A serialized diagnostics report in the requested format.
 *
 * Returning the report as a string lets clients save it without duplicating
 * formatters or depending on the report's internal schema.
 */
export type GetDiagnosticsResponse = string;

export const codecForGetDiagnosticsRequest = (): Codec<GetDiagnosticsRequest> =>
  buildCodecForObject<GetDiagnosticsRequest>()
    .property(
      "format",
      codecOptional(
        codecForEither(
          codecForConstString("json"),
          codecForConstString("yaml"),
        ),
      ),
    )
    .property("transactionLimit", codecOptional(codecForNumber()))
    .property(
      "frontendInfo",
      codecOptional(
        buildCodecForObject<DiagnosticsFrontendInfo>()
          .property("name", codecForString())
          .property("version", codecForString())
          .property("platform", codecOptional(codecForString()))
          .build("DiagnosticsFrontendInfo"),
      ),
    )
    .build("GetDiagnosticsRequest");

/** @deprecated Use {@link GetDiagnosticsResponse}. */
export type TestingGetDiagnosticsResponse = GetDiagnosticsResponse;

export interface TestingGetFlightRecordsResponse {
  flightRecords: FlightRecordEntry[];
}

export const codecForTestingGetDenomStatsRequest =
  (): Codec<TestingGetDenomStatsRequest> =>
    buildCodecForObject<TestingGetDenomStatsRequest>()
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .build("TestingGetDenomStatsRequest");

export interface RunFixupRequest {
  id: string;
}

export const codecForRunFixupRequest = (): Codec<RunFixupRequest> =>
  buildCodecForObject<RunFixupRequest>()
    .property("id", codecForString())
    .build("RunFixupRequest");

export interface WithdrawalExchangeAccountDetails {
  /**
   * Payto URI to of the exchange.
   *
   * Depending on whether the (manual!) withdrawal is accepted or just
   * being checked, this already includes the subject with the
   * reserve public key.
   *
   * Wallet UIs should never show instructions to send money to the exchange's payto
   */
  paytoUri: string;

  /**
   * Status that indicates whether the account can be used
   * by the user to send funds for a withdrawal.
   *
   * ok: account should be shown to the user
   * error: account should not be shown to the user, UIs might render the error (in conversionError),
   *   especially in dev mode.
   */
  status: "ok" | "error";

  /**
   * Transfer amount. Might be in a different currency than the requested
   * amount for withdrawal.
   *
   * Absent if this is a conversion account and the conversion failed.
   */
  transferAmount?: AmountString;

  /**
   * Currency specification for the external currency.
   *
   * Only included if this account requires a currency conversion.
   */
  currencySpecification?: CurrencySpecification;

  /**
   * Further restrictions for sending money to the
   * exchange.
   */
  creditRestrictions?: AccountRestriction[];

  /**
   * Label given to the account or the account's bank by the exchange.
   */
  bankLabel?: string;

  /*
   * Display priority assigned to this bank account by the exchange.
   */
  priority?: number;

  /**
   * Error that happened when attempting to request the conversion rate.
   */
  conversionError?: TalerErrorDetail;

  /**
   * Timestamp that indicates when the transfer options expire.
   *
   * If missing, options do not expire.
   */
  transferExpiry?: TalerProtocolTimestamp;

  /**
   * Options for transfering funds to the exchange for the withdrawal.
   */
  transferOptions: TransferOption[];
}

export type TransferOption =
  | TransferOptionPayto
  | TransferOptionUri
  | TransferOptionSwissQrBill;

export interface TransferOptionPayto {
  type: "payto";
  paytoUri: string;
  qrCodes: QrCodeSpec[];
}

export interface TransferOptionUri {
  type: "uri";
  uri: string;
}

export interface TransferOptionSwissQrBill {
  type: "ch-qr-bill";
  paytoUri: string;
  qrReferenceNumber: string;
  qrCodes: QrCodeSpec[];
}

/**
 * Raw transfer option, without QR code.
 */
export type TransferOptionRaw =
  | TransferOptionRawPayto
  | TransferOptionRawUri
  | TransferOptionRawSwissQrBill;

export interface TransferOptionRawPayto {
  type: "payto";
  paytoUri: string;
}

export interface TransferOptionRawUri {
  type: "uri";
  uri: string;
}

export interface TransferOptionRawSwissQrBill {
  type: "ch-qr-bill";
  paytoUri: string;
  qrReferenceNumber: string;
}

export interface PrepareWithdrawExchangeRequest {
  /**
   * A taler://withdraw-exchange URI.
   */
  talerUri: string;

  progressToken?: string;
}

export const codecForPrepareWithdrawExchangeRequest =
  (): Codec<PrepareWithdrawExchangeRequest> =>
    buildCodecForObject<PrepareWithdrawExchangeRequest>()
      .property("talerUri", codecForString())
      .property("progressToken", codecOptional(codecForString()))
      .build("PrepareWithdrawExchangeRequest");

export interface PrepareWithdrawExchangeResponse {
  /**
   * Base URL of the exchange that already existed
   * or was ephemerally added as an exchange entry to
   * the wallet.
   */
  exchangeBaseUrl: string;

  /**
   * Amount from the taler://withdraw-exchange URI.
   * Only present if specified in the URI.
   */
  amount?: AmountString;
}

export interface ExchangeEntryState {
  tosStatus: ExchangeTosStatus;
  exchangeEntryStatus: ExchangeEntryStatus;
  exchangeUpdateStatus: ExchangeUpdateStatus;
}

export interface ListGlobalCurrencyAuditorsResponse {
  auditors: {
    currency: string;
    auditorBaseUrl: string;
    auditorPub: string;
  }[];
}

export interface ListGlobalCurrencyExchangesResponse {
  exchanges: {
    currency: string;
    exchangeBaseUrl: string;
    exchangeMasterPub: string;
  }[];
}

export interface AddGlobalCurrencyExchangeRequest {
  currency: string;
  exchangeBaseUrl: string;
  exchangeMasterPub: string;
}

export const codecForAddGlobalCurrencyExchangeRequest =
  (): Codec<AddGlobalCurrencyExchangeRequest> =>
    buildCodecForObject<AddGlobalCurrencyExchangeRequest>()
      .property("currency", codecForString())
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .property("exchangeMasterPub", codecForString())
      .build("AddGlobalCurrencyExchangeRequest");

export interface RemoveGlobalCurrencyExchangeRequest {
  currency: string;
  exchangeBaseUrl: string;
  exchangeMasterPub: string;
}

export const codecForRemoveGlobalCurrencyExchangeRequest =
  (): Codec<RemoveGlobalCurrencyExchangeRequest> =>
    buildCodecForObject<RemoveGlobalCurrencyExchangeRequest>()
      .property("currency", codecForString())
      .property("exchangeBaseUrl", codecForCanonBaseUrl())
      .property("exchangeMasterPub", codecForString())
      .build("RemoveGlobalCurrencyExchangeRequest");

export interface AddGlobalCurrencyAuditorRequest {
  currency: string;
  auditorBaseUrl: string;
  auditorPub: string;
}

export const codecForAddGlobalCurrencyAuditorRequest =
  (): Codec<AddGlobalCurrencyAuditorRequest> =>
    buildCodecForObject<AddGlobalCurrencyAuditorRequest>()
      .property("currency", codecForString())
      .property("auditorBaseUrl", codecForCanonBaseUrl())
      .property("auditorPub", codecForString())
      .build("AddGlobalCurrencyAuditorRequest");

export interface RemoveGlobalCurrencyAuditorRequest {
  currency: string;
  auditorBaseUrl: string;
  auditorPub: string;
}

export const codecForRemoveGlobalCurrencyAuditorRequest =
  (): Codec<RemoveGlobalCurrencyAuditorRequest> =>
    buildCodecForObject<RemoveGlobalCurrencyAuditorRequest>()
      .property("currency", codecForString())
      .property("auditorBaseUrl", codecForCanonBaseUrl())
      .property("auditorPub", codecForString())
      .build("RemoveGlobalCurrencyAuditorRequest");

export interface HintNetworkAvailabilityRequest {
  isNetworkAvailable: boolean;
}

export const codecForHintNetworkAvailabilityRequest =
  (): Codec<HintNetworkAvailabilityRequest> =>
    buildCodecForObject<HintNetworkAvailabilityRequest>()
      .property("isNetworkAvailable", codecForBoolean())
      .build("HintNetworkAvailabilityRequest");

export interface GetDepositWireTypesRequest {
  currency?: string;
  /**
   * Optional scope info to further restrict the result.
   * Currency must match the currency field.
   */
  scopeInfo?: ScopeInfo;
}

export const codecForGetDepositWireTypesRequest =
  (): Codec<GetDepositWireTypesRequest> =>
    buildCodecForObject<GetDepositWireTypesRequest>()
      .property("currency", codecOptional(codecForString()))
      .property("scopeInfo", codecOptional(codecForScopeInfo()))
      .build("GetDepositWireTypesRequest");

export interface GetDepositWireTypesResponse {
  /**
   * Details for each wire type.
   */
  wireTypeDetails: WireTypeDetails[];
}

export interface GetDepositWireTypesForCurrencyRequest {
  currency: string;
  /**
   * Optional scope info to further restrict the result.
   * Currency must match the currency field.
   */
  scopeInfo?: ScopeInfo;
}

export const codecForGetDepositWireTypesForCurrencyRequest =
  (): Codec<GetDepositWireTypesForCurrencyRequest> =>
    buildCodecForObject<GetDepositWireTypesForCurrencyRequest>()
      .property("currency", codecForString())
      .property("scopeInfo", codecOptional(codecForScopeInfo()))
      .build("GetDepositWireTypesForCurrencyRequest");

/**
 * Response with wire types that are supported for a deposit.
 *
 * In the future, we might surface more information here, such as debit restrictions
 * by the exchange, which then can be shown by UIs to the user before they
 * enter their payment information.
 */
export interface GetDepositWireTypesForCurrencyResponse {
  /**
   * @deprecated, use wireTypeDetails instead.
   */
  wireTypes: string[];

  /**
   * Details for each wire type.
   */
  wireTypeDetails: WireTypeDetails[];
}

export interface WireTypeDetails {
  paymentTargetType: string;

  /**
   * Only applicable for payment target type IBAN.
   *
   * Specifies whether the user wants to preferably
   * enter their bank account details as an IBAN
   * or as a BBAN.
   *
   * Mandatory for paymentTargetType="iban".
   */
  preferredEntryType?: "iban" | "bban";

  /**
   * Allowed hostnames for the deposit payto URI.
   * Only applicable to x-taler-bank.
   */
  talerBankHostnames?: string[];
}

export interface GetQrCodesForPaytoRequest {
  paytoUri: string;
}

export const codecForGetQrCodesForPaytoRequest =
  (): Codec<GetQrCodesForPaytoRequest> =>
    buildCodecForObject<GetQrCodesForPaytoRequest>()
      .property("paytoUri", codecForString())
      .build("GetQrCodesForPaytoRequest");

export interface GetQrCodesForPaytoResponse {
  codes: QrCodeSpec[];
}

export interface GetBankingChoicesForPaytoRequest {
  paytoUri: string;
}

export const codecForGetBankingChoicesForPaytoRequest =
  (): Codec<GetBankingChoicesForPaytoRequest> =>
    buildCodecForObject<GetBankingChoicesForPaytoRequest>()
      .property("paytoUri", codecForString())
      .build("GetBankingChoicesForPaytoRequest");

export interface ConvertIbanAccountFieldToPaytoRequest {
  value: string;
  currency: string;
}

export const codecForConvertIbanAccountFieldToPaytoRequest =
  (): Codec<ConvertIbanAccountFieldToPaytoRequest> =>
    buildCodecForObject<ConvertIbanAccountFieldToPaytoRequest>()
      .property("value", codecForString())
      .property("currency", codecForString())
      .build("ConvertIbanAccountFieldToPaytoRequest");

export interface ConvertIbanPaytoToAccountFieldRequest {
  paytoUri: string;
}

export const codecForConvertIbanPaytoToAccountFieldRequest =
  (): Codec<ConvertIbanPaytoToAccountFieldRequest> =>
    buildCodecForObject<ConvertIbanPaytoToAccountFieldRequest>()
      .property("paytoUri", codecForString())
      .build("ConvertIbanPaytoToAccountFieldRequest");

export interface ConvertIbanPaytoToAccountFieldResponse {
  type: "iban" | "bban";
  value: string;
}

export type ConvertIbanAccountFieldToPaytoResponse =
  | { ok: true; type: "iban" | "bban"; paytoUri: string }
  | { ok: false };

export interface BankingChoiceSpec {
  label: string;
  // FIXME: In the future, we might also have some way to return intents here?
  type: "link";
  uri: string;
}

export interface GetBankingChoicesForPaytoResponse {
  choices: BankingChoiceSpec[];
}

export interface GetPerformanceStatsRequest {
  /**
   * Limit to N largest average performance stats of each table.
   *
   * When undefined, all performance stats will be returned.
   */
  limit?: number;
}

export const codecForGetPerformanceStatsRequest =
  (): Codec<GetPerformanceStatsRequest> =>
    buildCodecForObject<GetPerformanceStatsRequest>()
      .property("limit", codecOptional(codecForNumber()))
      .build("GetPerformanceStatsRequest");

export interface GetPerformanceStatsResponse {
  stats: PerformanceTable;
}

export type EmptyObject = Record<string, never>;

export const codecForEmptyObject = (): Codec<EmptyObject> =>
  buildCodecForObject<EmptyObject>().build("EmptyObject");

export interface TestingWaitWalletKycRequest {
  exchangeBaseUrl: string;
  amount: AmountString;
  /**
   * Do we wait for the KYC to be passed (true),
   * or do we already return if legitimization is
   * required (false).
   */
  passed: boolean;
}

export const codecForTestingWaitWalletKycRequest =
  (): Codec<TestingWaitWalletKycRequest> =>
    buildCodecForObject<TestingWaitWalletKycRequest>()
      .property("exchangeBaseUrl", codecForString())
      .property("amount", codecForAmountString())
      .property("passed", codecForBoolean())
      .build("TestingWaitWalletKycRequest");

export interface TestingPlanMigrateExchangeBaseUrlRequest {
  oldExchangeBaseUrl: string;
  newExchangeBaseUrl: string;
}

export const codecForTestingPlanMigrateExchangeBaseUrlRequest =
  (): Codec<TestingPlanMigrateExchangeBaseUrlRequest> =>
    buildCodecForObject<TestingPlanMigrateExchangeBaseUrlRequest>()
      .property("oldExchangeBaseUrl", codecForString())
      .property("newExchangeBaseUrl", codecForString())
      .build("TestingMigrateExchangeBaseUrlRequest");

export interface StartExchangeWalletKycRequest {
  exchangeBaseUrl: string;
  amount: AmountString;
}

export const codecForStartExchangeWalletKycRequest =
  (): Codec<StartExchangeWalletKycRequest> =>
    buildCodecForObject<StartExchangeWalletKycRequest>()
      .property("exchangeBaseUrl", codecForString())
      .property("amount", codecForAmountString())
      .build("StartExchangeWalletKycRequest");

export interface ExportDbToFileRequest {
  /**
   * Directory that the DB should be exported into.
   */
  directory: string;

  /**
   * Stem of the exported DB filename.
   *
   * The final name will be ${directory}/${stem}.${extension},
   * where the extension depends on the used DB backend.
   */
  stem: string;

  /**
   * Force the format of the export.
   *
   * Supported values on filesystem-capable hosts are "json" and "sqlite3".
   * If omitted, the host's default is "sqlite3".
   */
  forceFormat?: string;
}

export const codecForExportDbToFileRequest = (): Codec<ExportDbToFileRequest> =>
  buildCodecForObject<ExportDbToFileRequest>()
    .property("directory", codecForString())
    .property("stem", codecForString())
    .property("forceFormat", codecOptional(codecForString()))
    .build("ExportDbToFileRequest");

export interface ExportDbToFileResponse {
  /**
   * Full path to the backup.
   */
  path: string;
}

export interface ImportDbFromFileRequest {
  /**
   * Full path to a .json or .sqlite3 backup.
   */
  path: string;

  /** Correlates progress notifications and allows cancellation. */
  progressToken?: string;
}

export const codecForImportDbFromFileRequest =
  (): Codec<ImportDbFromFileRequest> =>
    buildCodecForObject<ImportDbFromFileRequest>()
      .property("path", codecForString())
      .property("progressToken", codecOptional(codecForString()))
      .build("ImportDbFromFileRequest");

export interface CompleteBaseUrlRequest {
  url: string;

  progressToken?: string;
}

export const codecForCompleteBaseUrlRequest =
  (): Codec<CompleteBaseUrlRequest> =>
    buildCodecForObject<CompleteBaseUrlRequest>()
      .property("url", codecForString())
      .property("progressToken", codecOptional(codecForString()))
      .build("CompleteBaseUrlRequest");

export type CompleteBaseUrlResult =
  | {
      /**
       * ok: completion is a proper exchange
       */
      status: "ok";
      /** Completed exchange base URL, if completion was possible */
      completion: string;
    }
  | {
      /**
       * bad-syntax: url is so badly malformed, it can't be completed
       * bad-network: syntax okay, but exchange can't be reached
       * bad-exchange: syntax and network okay, but not talking to an exchange
       */
      status: "bad-syntax" | "bad-network" | "bad-exchange";
      /** Error details in case status is not "ok" */
      error: TalerErrorDetail;

      /**
       * Base URLs of exchanges known to the wallet whose host looks like what
       * the user meant to type, most likely first.
       *
       * Absent when the wallet does not know anything similar.
       */
      suggestions?: string[];
    };

export interface SetDonauRequest {
  donauBaseUrl: string;
  taxPayerId: string;
}

export const codecForSetDonauRequest = (): Codec<SetDonauRequest> =>
  buildCodecForObject<SetDonauRequest>()
    .property("donauBaseUrl", codecForString())
    .property("taxPayerId", codecForString())
    .build("SetDonauRequest");

export interface DonauStatementItem {
  total: AmountString;
  year: number;
  legalDomain: string;
  uri: string;
  donationStatementSig: EddsaSignatureString;
  donauPub: EddsaPublicKeyString;
}

export interface GetDonauStatementsRequest {
  donauBaseUrl?: string;
}

export interface GetDonauStatementsResponse {
  statements: DonauStatementItem[];
}

export interface GetDonauResponse {
  currentDonauInfo:
    | {
        donauBaseUrl: string;
        taxPayerId: string;
      }
    | undefined;
}

export const codecForGetDonauStatementsRequest =
  (): Codec<GetDonauStatementsRequest> =>
    buildCodecForObject<GetDonauStatementsRequest>()
      .property("donauBaseUrl", codecOptional(codecForString()))
      .build("GetDonauStatementsRequest");

export interface FlightRecordEntry {
  timestamp: TalerPreciseTimestamp;
  target: string;
  event: FlightRecordEvent;
}

export enum FlightRecordEvent {
  MeltGone = "melt-gone",
  WithdrawalRedenominate = "withdrawal-redenominate",
}

/**
 * @deprecated Use {@link ListWithdrawalExchangeCandidatesRequest} instead.
 */
export interface GetDefaultExchangesRequest {
  /**
   * Only return production exchanges from the builtin exchange list.
   * Cannot be combined with `withBuiltin: false`.
   */
  presetOnly?: boolean;

  /** Include exchanges from the builtin list.  Defaults to true. */
  withBuiltin?: boolean;

  /** Include demo exchanges from the builtin list.  Defaults to false. */
  withDemo?: boolean;

  /** Include test exchanges from the builtin list.  Defaults to false. */
  withTest?: boolean;
}

export const codecForGetDefaultExchangesRequest =
  (): Codec<GetDefaultExchangesRequest> =>
    buildCodecForObject<GetDefaultExchangesRequest>()
      .property("presetOnly", codecOptional(codecForBoolean()))
      .property("withBuiltin", codecOptional(codecForBoolean()))
      .property("withDemo", codecOptional(codecForBoolean()))
      .property("withTest", codecOptional(codecForBoolean()))
      .build("GetDefaultExchangesRequest");

/**
 * @deprecated Use {@link ListWithdrawalExchangeCandidatesResponse} instead.
 */
export interface GetDefaultExchangesResponse {
  defaultExchanges: {
    /**
     * A taler://withdraw-exchange URI for the
     * exchange.
     */
    talerUri: string;
    /**
     * Currency offered by the exchange.
     */
    currency: string;
    /**
     * Currency spec for the currency offered
     * by the exchange.
     */
    currencySpec: CurrencySpecification;
  }[];
}

export type ListWithdrawalExchangeCandidatesRequest =
  GetDefaultExchangesRequest;

export const codecForListWithdrawalExchangeCandidatesRequest =
  (): Codec<ListWithdrawalExchangeCandidatesRequest> =>
    buildCodecForObject<ListWithdrawalExchangeCandidatesRequest>()
      .property("presetOnly", codecOptional(codecForBoolean()))
      .property("withBuiltin", codecOptional(codecForBoolean()))
      .property("withDemo", codecOptional(codecForBoolean()))
      .property("withTest", codecOptional(codecForBoolean()))
      .build("ListWithdrawalExchangeCandidatesRequest");

export enum ExchangeRecommendationReason {
  Preset = "preset",
  UserAdded = "user-added",
  PreviousWithdrawal = "previous-withdrawal",
  PreviouslyUsed = "previously-used",
}

export interface WithdrawalExchangeCandidate {
  /** A taler://withdraw-exchange URI for the exchange. */
  talerUri: string;
  exchangeBaseUrl: string;
  currency: string;
  currencySpec: CurrencySpecification;
  exchangeEntryStatus: ExchangeEntryStatus;
  exchangeUpdateStatus: ExchangeUpdateStatus;
  source: ExchangeEntrySource;
  recommendationReasons: ExchangeRecommendationReason[];
  lastWithdrawal?: TalerPreciseTimestamp;
}

export interface ListWithdrawalExchangeCandidatesResponse {
  candidates: WithdrawalExchangeCandidate[];
}

export interface TestingCorruptWithdrawalCoinSelRequest {
  transactionId: TransactionIdStr;
}

export const codecForTestingCorruptWithdrawalCoinSelRequest =
  (): Codec<TestingCorruptWithdrawalCoinSelRequest> =>
    buildCodecForObject<TestingCorruptWithdrawalCoinSelRequest>()
      .property("transactionId", codecForTransactionIdStr())
      .build("TestingCorruptWithdrawalCoinSelRequest");

export interface RetryProgressTokenNowRequest {
  operation: string;
  progressToken: string;
}

export const codecForRetryProgressTokenNowRequest =
  (): Codec<RetryProgressTokenNowRequest> =>
    buildCodecForObject<RetryProgressTokenNowRequest>()
      .property("progressToken", codecForString())
      .property("operation", codecForString())
      .build("RetryProgressTokenNowRequest");

export interface CancelProgressTokenRequest {
  operation: string;
  progressToken: string;
}

export const codecForCancelProgressToken =
  (): Codec<CancelProgressTokenRequest> =>
    buildCodecForObject<CancelProgressTokenRequest>()
      .property("progressToken", codecForString())
      .property("operation", codecForString())
      .build("CancelProgressTokenRequest");
